/** Pure HTML templates for the dashboard at dashboard.recued.com. */

import type { AccountBindingAuditAction, AccountBindingSummary } from '@recued/contracts';
import type { AuthUser } from './auth.js';

// ────────────────────────────────────────────────────────────────
// Types
// ────────────────────────────────────────────────────────────────

export type Page = 'signin' | 'home' | 'recipes' | 'packs' | 'publish' | 'account';
export type AuthMode = 'signin' | 'signup' | 'reset-request' | 'reset-sent' | 'reset-update' | 'confirm-email';
export type SessionStatus = 'checking' | 'unauthenticated' | 'authenticated' | 'error';
export type DashboardPlan = 'Free' | 'Pro';
export type BindingStatus = 'No server' | 'Bound' | 'Conflict';
export type BillingStatus = 'Free' | 'Active' | 'Past due' | 'Canceled' | 'Not configured';
export type ProConvenienceStatus = 'active' | 'awaiting-server' | 'not-configured' | 'error';
export type AccountBindingReadStatus = 'pending-worker-endpoint' | 'ready' | 'error';
export type SubmissionStatus = 'pending' | 'approved' | 'rejected' | 'cancelled';
export type RecipeStatusFilter = 'all' | 'published' | SubmissionStatus | 'unpublished';
export type RecipeSort = 'updated-desc' | 'name-asc' | 'status-asc';
/** Operation risk tier, surfaced by the publish-validation operation
 *  projection (shared across recipe + pack artifacts). */
export type OperationRiskTier = 'all' | 'read' | 'write' | 'admin' | 'destructive' | 'unknown';
export type PublishSource = 'kitchen' | 'upload' | 'paste';
export type PublishValidationSeverity = 'ok' | 'warning' | 'error' | 'pending';
export type PublishValidationKind = 'source' | 'schema' | 'marketplace-gate' | 'slug-reservation' | 'dependency';
export type SlugReservationState = 'available' | 'owned-by-this-account' | 'reserved-pending' | 'unavailable' | 'expiring';

export interface ProConvenienceItem {
  key: 'ddns' | 'acme' | 'reserved-handle';
  label: string;
  status: ProConvenienceStatus;
  detail: string;
}

/** One past Pro charge, projected for the billing-history list. `amountMinor`
 *  is the grand total in the currency's lowest denomination (cents for USD), as
 *  a string; the renderer formats it. */
export interface BillingTransactionView {
  status: string;
  occurredAt: string;
  amountMinor: string | null;
  currencyCode: string | null;
  invoiceNumber: string | null;
}

export interface AccountBindingAuditProjection {
  action: AccountBindingAuditAction;
  account_id: string;
  server_fingerprint: string;
  timestamp: number;
  actor_surface: string;
  outcome: string;
  confirmed?: boolean;
}

export interface AccountBindingReadModel {
  status: AccountBindingReadStatus;
  bindings: AccountBindingSummary[];
  auditEvents: AccountBindingAuditProjection[];
  error: string | null;
}

export interface BindingActionMessage {
  kind: 'success' | 'error';
  message: string;
}

export interface PublishedItem {
  slug?: string;
  recipe_id?: string;
  publisher_id: string;
  manifest?: Record<string, unknown>;
  recipe?: Record<string, unknown>;
  version_history?: Record<string, unknown>[];
  fork_of?: unknown;
  submitted_at?: string;
  approved_at?: string;
  reviewed_at?: string;
  published_at?: string;
  downloads?: number;
  download_count?: number;
  created_at?: string;
  updated_at?: string;
  version?: number;
  name?: string;
  description?: string;
  tags?: string[];
  platforms?: string[];
  rating_avg?: number | string;
  rating_count?: number;
  status?: string;
  slug_reservation_state?: SlugReservationState;
}

export interface DashboardState {
  page: Page;
  user: AuthUser | null;
  sessionStatus: SessionStatus;
  sessionError: string | null;
  // Auth form
  authMode: AuthMode;
  authEmail: string;
  authPassword: string;
  authError: string | null;
  authLoading: boolean;
  authNewPassword: string;
  /** Signup-only: must be true (Terms agreement) before account creation. */
  authAgreeTerms: boolean;
  resetToken: string | null;
  resetSuccess: string | null;
  /** SW-8 — the user just confirmed their email (returned via the confirmation
   *  link). Surfaces a "your email is confirmed, sign in" banner on the auth
   *  page; clears once they leave the sign-in flow. */
  emailConfirmedNotice: boolean;
  /** Resend-confirmation state on the "check your email" card. The cooldown
   *  (seconds remaining; 0 = ready) is the client-side timeout gate; GoTrue also
   *  rate-limits server-side. */
  resendStatus: 'idle' | 'sending' | 'sent' | 'error';
  resendMessage: string | null;
  resendCooldownRemaining: number;
  // Publisher
  publisherHandle: string | null;
  publisherInput: string;
  // R20 — publisher marketplace-presentation profile (website feeds the
  // marketplace About link; bio enriches the publisher page). Editable from
  // the Account → Publisher namespace section.
  publisherWebsite: string | null;
  publisherBio: string | null;
  profileEditing: boolean;
  profileWebsiteInput: string;
  profileBioInput: string;
  profileSaving: boolean;
  profileMessage: string | null;
  // AW-2 — marketplace handle rename (re-keys the publisher's artifact tree
  // server-side via ON UPDATE CASCADE).
  handleRenameOpen: boolean;
  handleRenameInput: string;
  handleRenameSaving: boolean;
  handleRenameMessage: string | null;
  plan: DashboardPlan;
  bindingStatus: BindingStatus;
  billingStatus: BillingStatus;
  billingRenewalLabel: string | null;
  checkoutStatus: 'idle' | 'starting' | 'error';
  checkoutMessage: string | null;
  /** DB-7a — the user just returned from the Paddle checkout / customer portal.
   *  `success` confirms payment (the webhook activates Pro shortly); `cancelled`
   *  notes an abandoned checkout. Dismissible; clears on reload. */
  checkoutReturn: 'success' | 'cancelled' | null;
  /** A period-end cancellation is scheduled — Pro stays until the date, then
   *  drops to Free. Drives the cancel/resume button toggle + notice banner. */
  cancelScheduled: boolean;
  /** The cloud holds a Paddle subscription id for this account, so cancel /
   *  resume can be self-served. False (legacy / portal-created) → portal only. */
  subscriptionManageable: boolean;
  /** A cancel/resume/portal billing action is in flight. */
  billingActionStatus: 'idle' | 'cancelling' | 'resuming' | 'opening-portal';
  billingActionMessage: string | null;
  /** The inline "confirm cancel" panel is open. */
  billingCancelConfirm: boolean;
  /** Past Pro charges (Paddle transactions), lazily loaded on demand. `null`
   *  until the user opens the history; `[]` is a loaded-but-empty result. */
  billingHistory: BillingTransactionView[] | null;
  billingHistoryStatus: 'idle' | 'loading' | 'loaded' | 'error';
  billingHistoryMessage: string | null;
  /** Cursor of the currently-shown history page (`null` = first page) — echoed
   *  to the invoice endpoint so it re-fetches the same page. */
  billingHistoryCursor: string | null;
  /** Next-page cursor, or `null` when this is the last page (drives "Next"). */
  billingHistoryNextCursor: string | null;
  /** Back-stack of page cursors visited, for "Previous". */
  billingHistoryPrevCursors: (string | null)[];
  /** Index of the history row whose invoice-PDF link is being minted, or `null`.
   *  Drives the per-row download button's busy state. */
  billingInvoiceIndex: number | null;
  billingInvoiceMessage: string | null;
  proConveniences: ProConvenienceItem[];
  accountBindingRead: AccountBindingReadModel;
  bindingUnbindingId: string | null;
  /** Fingerprint whose credential-rotate call is in flight. */
  bindingRotatingId: string | null;
  bindingActionMessages: Record<string, BindingActionMessage>;
  // Security card
  signOutAllStatus: 'idle' | 'working';
  signOutAllMessage: string | null;
  // Data
  myRecipes: PublishedItem[];
  myPacks: PublishedItem[];
  loading: boolean;
  error: string | null;
  /** `${type}:${id}` of the item whose unpublish/republish PATCH is in
   *  flight; the row's action button renders busy. */
  itemStatusUpdatingKey: string | null;
  /** Per-item action errors, keyed `${type}:${id}`. */
  itemStatusErrors: Record<string, string>;
  // Metadata editor (repo URL + description) — one open editor at a time.
  /** `${type}:${id}` of the item being edited; null = closed. */
  editingItemKey: string | null;
  editRepoInput: string;
  editDescriptionInput: string;
  /** Values at editor-open; only changed fields are sent on save. */
  editInitialRepo: string;
  editInitialDescription: string;
  metaSaving: boolean;
  metaError: string | null;
  // Publish form
  /** Section tab on #/publish — the publish form vs the in-review records list. */
  publishTab: 'publish' | 'review';
  publishType: 'recipe' | 'pack';
  publishSource: PublishSource;
  publishJson: string;
  publishStatus: 'idle' | 'publishing' | 'success' | 'error';
  publishMessage: string | null;
  /** Non-null when the user arrived from the extension via ?return=
   *  and successfully signed in. The template renders a "Return to
   *  Recued Kitchen" link so they can resume their publish flow. */
  returnToExtensionUrl: string | null;
  // Submissions
  submissions: SubmissionItem[];
  submissionCancellingId: string | null;
  submissionActionErrors: Record<string, string>;
  // Publisher recipe console
  recipeSearch: string;
  recipeStatusFilter: RecipeStatusFilter;
  recipeSort: RecipeSort;
  selectedRecipeKey: string | null;
  copyStatus: string | null;
  // Account deletion (danger zone) — immediate + irreversible.
  /** Confirmation panel open. */
  deleteAccountOpen: boolean;
  /** Typed-email confirmation field. */
  deleteAccountConfirm: string;
  deleteAccountStatus: 'idle' | 'deleting' | 'error';
  deleteAccountMessage: string | null;
}

export interface SubmissionItem {
  review_id: string;
  type: 'recipe' | 'pack';
  slug: string;
  version: number;
  status: SubmissionStatus;
  name: string;
  submitted_at: string;
  reviewed_at: string | null;
  rejection_reason: string | null;
  rejection_reasons?: string[];
  rejection_reason_at?: string | null;
  expires_at: string | null;
  cancelable_until?: string | null;
  cancellation_allowed?: boolean | null;
  slug_reservation_state?: SlugReservationState | null;
  previous_version?: number | null;
  source_slug?: string | null;
  fork_of?: unknown;
}

export interface PublishValidationIssue {
  severity: PublishValidationSeverity;
  kind: PublishValidationKind;
  label: string;
  detail: string;
  path?: string;
  code?: string;
}

export interface PublishDraftValidation {
  parsed: Record<string, unknown> | null;
  issues: PublishValidationIssue[];
  slug: string | null;
  rewrittenSlug: string | null;
  name: string | null;
  description: string | null;
  version: string;
  tags: string[];
  platforms: string[];
  executionScope: string;
  riskSummary: string;
  approvalSummary: string;
  provenanceLabel: string;
  forkLineage: string | null;
  dependencySlugs: string[];
  slugReservationState: SlugReservationState | null;
  slugReservationDetail: string | null;
  canSubmit: boolean;
}

export interface PublishValidationContext {
  submissions?: SubmissionItem[];
  myRecipes?: PublishedItem[];
  myPacks?: PublishedItem[];
  now?: number;
}

// There is deliberately no BILLING_PORTAL_URL constant. The one that lived here
// (`https://billing.recued.com/portal`) was never backed by anything — no app, no
// worker, no wrangler route, NXDOMAIN on the zone's authoritative nameserver — and
// it was the target of every "Manage billing" control in this file. Paddle's
// customer portal has no static address by design: each session is minted per
// customer via POST /customers/<id>/portal-sessions, which needs an authenticated
// request. So the control is a button (`data-action="open-billing-portal"`), never
// an href. See handleOpenBillingPortal in dashboard.ts.
// The apex worker serves the marketplace at clean root URLs (D-180); the
// retired `/marketplace` path-prefix 301-redirects, so link at the root.
export const MARKETPLACE_URL = 'https://recued.com';
export const KITCHEN_URL = 'https://app.recued.com/#kitchen';
export const APP_URL = 'https://app.recued.com';

export const pendingAccountBindingRead = (): AccountBindingReadModel => ({
  status: 'pending-worker-endpoint',
  bindings: [],
  auditEvents: [],
  error: null,
});

export const defaultProConveniences = (
  plan: DashboardPlan,
  bindingStatus: BindingStatus,
  publisherHandle: string | null,
): ProConvenienceItem[] => {
  const serverStatus: ProConvenienceStatus = plan === 'Pro' && bindingStatus === 'No server'
    ? 'awaiting-server'
    : 'not-configured';
  return [
    {
      key: 'ddns',
      label: '<handle>.recued.net DDNS',
      status: serverStatus,
      detail: serverStatus === 'awaiting-server'
        ? 'Awaiting a bound server before DDNS can be issued.'
        : 'Not configured.',
    },
    {
      key: 'acme',
      label: 'ACME certificate',
      status: serverStatus,
      detail: serverStatus === 'awaiting-server'
        ? 'Awaiting a bound server before certificate issuance.'
        : 'Not configured.',
    },
    {
      key: 'reserved-handle',
      label: 'Reserved marketplace handle',
      status: 'not-configured',
      detail: publisherHandle
        ? `Publisher namespace @${publisherHandle} is set; Pro reservation state is not configured.`
        : 'No publisher namespace is reserved yet.',
    },
  ];
};

export const initialState = (): DashboardState => ({
  page: 'signin',
  user: null,
  sessionStatus: 'unauthenticated',
  sessionError: null,
  authMode: 'signin',
  authEmail: '',
  authPassword: '',
  authError: null,
  authLoading: false,
  authNewPassword: '',
  authAgreeTerms: false,
  resetToken: null,
  resetSuccess: null,
  emailConfirmedNotice: false,
  resendStatus: 'idle',
  resendMessage: null,
  resendCooldownRemaining: 0,
  publisherHandle: null,
  publisherInput: '',
  publisherWebsite: null,
  publisherBio: null,
  profileEditing: false,
  profileWebsiteInput: '',
  profileBioInput: '',
  profileSaving: false,
  profileMessage: null,
  handleRenameOpen: false,
  handleRenameInput: '',
  handleRenameSaving: false,
  handleRenameMessage: null,
  plan: 'Free',
  bindingStatus: 'No server',
  billingStatus: 'Free',
  billingRenewalLabel: null,
  checkoutStatus: 'idle',
  checkoutMessage: null,
  checkoutReturn: null,
  cancelScheduled: false,
  subscriptionManageable: false,
  billingActionStatus: 'idle',
  billingActionMessage: null,
  billingCancelConfirm: false,
  billingHistory: null,
  billingHistoryStatus: 'idle',
  billingHistoryMessage: null,
  billingHistoryCursor: null,
  billingHistoryNextCursor: null,
  billingHistoryPrevCursors: [],
  billingInvoiceIndex: null,
  billingInvoiceMessage: null,
  proConveniences: defaultProConveniences('Free', 'No server', null),
  accountBindingRead: pendingAccountBindingRead(),
  bindingUnbindingId: null,
  bindingRotatingId: null,
  bindingActionMessages: {},
  signOutAllStatus: 'idle',
  signOutAllMessage: null,
  myRecipes: [],
  myPacks: [],
  loading: false,
  error: null,
  itemStatusUpdatingKey: null,
  itemStatusErrors: {},
  editingItemKey: null,
  editRepoInput: '',
  editDescriptionInput: '',
  editInitialRepo: '',
  editInitialDescription: '',
  metaSaving: false,
  metaError: null,
  publishTab: 'publish',
  publishType: 'recipe',
  publishSource: 'kitchen',
  publishJson: '',
  publishStatus: 'idle',
  publishMessage: null,
  returnToExtensionUrl: null,
  submissions: [],
  submissionCancellingId: null,
  submissionActionErrors: {},
  recipeSearch: '',
  recipeStatusFilter: 'all',
  recipeSort: 'updated-desc',
  selectedRecipeKey: null,
  copyStatus: null,
  deleteAccountOpen: false,
  deleteAccountConfirm: '',
  deleteAccountStatus: 'idle',
  deleteAccountMessage: null,
});

// ────────────────────────────────────────────────────────────────
// Escape
// ────────────────────────────────────────────────────────────────

const ESC: Record<string, string> = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' };
export const e = (s: string): string => s.replace(/[&<>"']/g, (c) => ESC[c] ?? c);

// ────────────────────────────────────────────────────────────────
// Shell
// ────────────────────────────────────────────────────────────────

const NAV_ITEMS: Array<[Page, string]> = [
  ['home', 'Dashboard'],
  ['recipes', 'Recipes'],
  ['packs', 'Packs'],
  ['publish', 'Publish'],
  ['account', 'Account'],
];

const chipClass = (value: string): string => value.toLowerCase().replace(/[^a-z0-9]+/g, '-');

const safeExternalUrl = (value: string, fallback: string): string => {
  try {
    const url = new URL(value);
    if (url.protocol === 'https:') return url.toString();
  } catch {
    /* fall through */
  }
  return fallback;
};

const renderNav = (state: DashboardState): string => {
  if (!state.user) return '';
  const link = (page: Page, label: string) =>
    `<a href="#/${page}" class="nav-link ${state.page === page ? 'active' : ''}"
      ${state.page === page ? 'aria-current="page"' : ''}>${label}</a>`;
  return `
    <nav class="dash-nav" aria-label="Dashboard sections">
      ${NAV_ITEMS.map(([page, label]) => link(page, label)).join('')}
    </nav>
  `;
};

const renderBrand = (): string => `
  <a href="#/" class="dash-brand" aria-label="Recued dashboard home">
    <span class="dash-brand-mark" aria-hidden="true"></span>
    <span>Recued</span>
    <span class="dash-brand-surface">Publisher</span>
  </a>
`;

const renderHeader = (state: DashboardState): string => {
  if (state.sessionStatus === 'checking') {
    return `
      <header class="dash-header">
        <div class="dash-header-inner">
          ${renderBrand()}
          <div class="dash-nav skeleton-nav" aria-hidden="true">
            <span class="skeleton-line"></span>
            <span class="skeleton-line"></span>
            <span class="skeleton-line"></span>
          </div>
          <div class="dash-user skeleton-user" aria-hidden="true">
            <span class="skeleton-pill"></span>
            <span class="skeleton-pill"></span>
          </div>
        </div>
      </header>
    `;
  }

  return `
    <header class="dash-header">
      <div class="dash-header-inner">
        ${renderBrand()}
        ${renderNav(state)}
        ${state.user ? `
          <div class="dash-user" aria-label="Account">
            <a href="#/account" class="dash-account-menu" aria-label="Open account settings">
              <span class="dash-user-email">${e(state.user.email)}</span>
              <span class="dash-user-handle">${state.publisherHandle ? `@${e(state.publisherHandle)}` : 'No publisher'}</span>
            </a>
            <span class="chip chip-plan chip-${chipClass(state.plan)}">${e(state.plan)}</span>
            <span class="chip chip-binding chip-${chipClass(state.bindingStatus)}">${e(state.bindingStatus)}</span>
            <button type="button" class="btn btn-sm dash-sign-out" data-action="sign-out">Sign out</button>
          </div>
        ` : ''}
      </div>
    </header>
  `;
};

/** "Manage billing" — a button, never a link. See the note where
 *  BILLING_PORTAL_URL used to live: Paddle's portal has no static address, only
 *  per-customer sessions minted server-side.
 *
 *  Rendered only when a stored subscription exists (`subscriptionManageable`).
 *  A Free account that never subscribed has no Paddle customer record, so the
 *  mint 409s — offering the control there would be a button that can only ever
 *  error. The Subscription card shows "Upgrade to Pro" in that state instead. */
const renderManageBillingButton = (state: DashboardState, variant: string): string => {
  if (!state.subscriptionManageable) return '';
  const opening = state.billingActionStatus === 'opening-portal';
  const busy = state.billingActionStatus !== 'idle';
  return `<button type="button" class="btn ${variant} btn-sm" data-action="open-billing-portal" ${
    opening ? 'disabled aria-busy="true"' : busy ? 'disabled' : ''
  }>${opening ? 'Opening billing portal' : 'Manage billing'}</button>`;
};

const renderGlobalActions = (state: DashboardState): string => {
  if (!state.user) return '';
  const publisherUrl = state.publisherHandle ? publisherPageUrl(state.publisherHandle) : '';
  return `
    <nav class="global-actions" aria-label="Workspace shortcuts">
      <span class="global-actions-label">Workspace</span>
      <div class="global-actions-scroll">
      <a href="${MARKETPLACE_URL}" target="_blank" rel="noopener" class="btn btn-quiet btn-sm">Browse marketplace</a>
      <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-quiet btn-sm" data-action="open-kitchen">Open Kitchen</a>
      ${renderManageBillingButton(state, 'btn-quiet')}
      <button type="button" class="btn btn-quiet btn-sm" data-action="copy-text"
        data-copy-value="${e(publisherUrl)}" data-copy-label="Publisher page link"
        ${publisherUrl ? '' : 'disabled'}><span class="action-label-long">Copy publisher-page link</span><span class="action-label-short" aria-hidden="true">Copy page link</span></button>
      </div>
    </nav>
  `;
};

const renderPageSkeleton = (): string => `
  <section class="dash-page page-skeleton" aria-busy="true">
    <span class="skeleton-line skeleton-title"></span>
    <div class="skeleton-grid">
      <span class="skeleton-card"></span>
      <span class="skeleton-card"></span>
      <span class="skeleton-card"></span>
    </div>
    <span class="skeleton-line skeleton-wide"></span>
    <span class="skeleton-line skeleton-mid"></span>
  </section>
`;

const renderSessionError = (state: DashboardState): string => `
  <section class="dash-page page-error">
    <h2>Dashboard unavailable</h2>
    <div class="flash flash-error">
      ${e(state.sessionError ?? 'Could not check the dashboard session.')}
    </div>
    <button type="button" class="btn btn-primary" data-action="retry-session">Retry</button>
  </section>
`;

// ────────────────────────────────────────────────────────────────
// Sign in / Sign up
// ────────────────────────────────────────────────────────────────

const renderAuth = (state: DashboardState): string => {
  const isSignup = state.authMode === 'signup';
  const isResetRequest = state.authMode === 'reset-request';
  const isResetSent = state.authMode === 'reset-sent';
  const isResetUpdate = state.authMode === 'reset-update';
  const isResetFlow = isResetRequest || isResetSent || isResetUpdate;
  // A signup (or an unconfirmed sign-in) that came back `no_session` — the
  // account exists but email confirmation is pending. A success, not an error.
  const isConfirmEmail = state.authMode === 'confirm-email';

  const errorBanner = state.authError ? `<div class="flash flash-error">${e(state.authError)}</div>` : '';
  const returnBanner = state.returnToExtensionUrl ? `
    <div class="flash flash-ok" style="text-align:center;">
      Signed in! <a href="${e(state.returnToExtensionUrl)}">Return to Recued Kitchen</a> to continue publishing.
    </div>
  ` : '';
  // After a password reset, show a richer card with sidebar instructions
  // instead of a one-line flash. The dashboard and extension share a
  // Supabase backend but not a session — users need to sign into each
  // surface once, and first-time visitors don't always know where the
  // extension's sign-in lives.
  const successBanner = state.resetSuccess ? `
    <div class="reset-success">
      <strong class="reset-success-title">Password updated</strong>
      <p class="reset-success-body">${e(state.resetSuccess)}</p>
      <div class="reset-success-hint">
        <p><strong>Using the Recued extension?</strong> Open the Recued sidebar on any tab and click <em>Sign in</em> in the top-right.</p>
        <p class="reset-success-fallback">Can't find the sidebar? Go to <code>chrome://extensions</code> → Recued → Details → Extension options → Account.</p>
      </div>
    </div>
  ` : '';
  // SW-8 — landing after the user clicks their email-confirmation link. They are
  // confirmed but not yet signed in (no session cookie), so we greet them on the
  // sign-in form rather than leaving a bare page.
  const emailConfirmedBanner = state.emailConfirmedNotice ? `
    <div class="flash flash-ok" style="text-align:center;" data-email-confirmed-notice>
      Your email is confirmed. Sign in to continue.
    </div>
  ` : '';

  let formBody = '';
  if (isResetUpdate) {
    formBody = `
      ${errorBanner}
      <div class="auth-fields">
        <p class="auth-hint">Enter your new password.</p>
        <input type="password" data-field="new-password" value="${e(state.authNewPassword)}"
          placeholder="New password (min 6 characters)" autocomplete="new-password" />
        <button type="button" class="btn btn-primary" data-action="reset-update"
          ${!state.authNewPassword || state.authNewPassword.length < 6 || state.authLoading ? 'disabled' : ''}>
          ${state.authLoading ? 'Updating\u2026' : 'Set new password'}
        </button>
      </div>`;
  } else if (isResetSent) {
    formBody = `
      <div class="auth-fields">
        <p class="auth-hint">Check your email for a password reset link. Click the link to set a new password.</p>
        <p class="auth-hint">Didn\u2019t receive it? Check spam, or resend.</p>
        <button type="button" class="btn" data-action="reset-request">Resend link</button>
        <button type="button" class="btn" data-action="auth-mode" data-mode="signin">Back to sign in</button>
      </div>`;
  } else if (isResetRequest) {
    formBody = `
      ${errorBanner}
      <div class="auth-fields">
        <p class="auth-hint">Enter your email and we\u2019ll send a reset link.</p>
        <input type="email" data-field="email" value="${e(state.authEmail)}"
          placeholder="email@example.com" autocomplete="email" />
        <button type="button" class="btn btn-primary" data-action="reset-request"
          ${!state.authEmail || state.authLoading ? 'disabled' : ''}>
          ${state.authLoading ? 'Sending\u2026' : 'Send reset link'}
        </button>
        <button type="button" class="btn" data-action="auth-mode" data-mode="signin">Back to sign in</button>
      </div>`;
  } else if (isConfirmEmail) {
    const resendLabel = state.resendStatus === 'sending'
      ? 'Sending\u2026'
      : state.resendCooldownRemaining > 0
        ? `Resend in ${state.resendCooldownRemaining}s`
        : 'Resend confirmation email';
    const resendDisabled = state.resendStatus === 'sending' || state.resendCooldownRemaining > 0;
    formBody = `
      <div class="auth-fields">
        <div class="flash flash-ok" style="text-align:center;" data-confirm-email-notice>
          Almost there \u2014 confirm your email.
        </div>
        <p class="auth-hint">We sent a confirmation link to <strong>${e(state.authEmail)}</strong>. Click it to activate your account, then sign in.</p>
        ${state.resendMessage
          ? `<p class="auth-hint" data-resend-message>${e(state.resendMessage)}</p>`
          : '<p class="auth-hint">Didn\u2019t get it? Check your spam folder, or resend below.</p>'}
        <button type="button" class="btn btn-secondary" data-action="resend-confirmation" ${resendDisabled ? 'disabled' : ''}>${e(resendLabel)}</button>
        <button type="button" class="btn-link" data-action="auth-mode" data-mode="signin">Back to sign in</button>
      </div>`;
  } else {
    formBody = `
      <div class="oauth-buttons">
        <button type="button" class="btn btn-oauth" data-action="oauth" data-provider="google">
          Continue with Google
        </button>
        <button type="button" class="btn btn-oauth" data-action="oauth" data-provider="github">
          Continue with GitHub
        </button>
      </div>
      <p class="auth-oauth-consent">
        By continuing, you agree to the
        <a href="https://recued.com/terms.html" target="_blank" rel="noopener">Terms of Service</a>
        and <a href="https://recued.com/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>.
      </p>
      <div class="auth-divider"><span>or</span></div>
      ${returnBanner}${errorBanner}${successBanner}${emailConfirmedBanner}
      <div class="auth-toggle">
        <button type="button" class="auth-tab ${!isSignup ? 'active' : ''}"
          data-action="auth-mode" data-mode="signin">Sign in</button>
        <button type="button" class="auth-tab ${isSignup ? 'active' : ''}"
          data-action="auth-mode" data-mode="signup">Sign up</button>
      </div>
      <div class="auth-fields">
        <input type="email" data-field="email" value="${e(state.authEmail)}"
          placeholder="email@example.com" autocomplete="email" />
        <input type="password" data-field="password" value="${e(state.authPassword)}"
          placeholder="Password" autocomplete="${isSignup ? 'new-password' : 'current-password'}" />
        ${isSignup ? `
        <label class="auth-terms">
          <input type="checkbox" data-field="agree-terms" ${state.authAgreeTerms ? 'checked' : ''} />
          <span>I agree to the
            <a href="https://recued.com/terms.html" target="_blank" rel="noopener">Terms of Service</a>
            and <a href="https://recued.com/privacy.html" target="_blank" rel="noopener">Privacy Policy</a>.</span>
        </label>` : ''}
        <button type="button" class="btn btn-primary" data-action="auth-submit"
          ${state.authLoading || !state.authEmail || !state.authPassword || (isSignup && !state.authAgreeTerms) ? 'disabled' : ''}>
          ${state.authLoading ? 'Loading\u2026' : isSignup ? 'Create account' : 'Sign in'}
        </button>
        ${!isSignup ? '<button type="button" class="btn-link" data-action="auth-mode" data-mode="reset-request">Forgot password?</button>' : ''}
      </div>`;
  }

  return `
    <main class="auth-page" id="dashboard-main">
      <section class="auth-intro" aria-label="About the Recued publisher dashboard">
        <span class="eyebrow">Publisher workspace</span>
        <h2>Ship finished work.<br />Keep authorship local.</h2>
        <p>Manage marketplace releases, review status, billing, and server binding without moving recipe authoring out of Kitchen.</p>
        <ul class="auth-value-list">
          <li><span aria-hidden="true">01</span><strong>Publish</strong><small>Validate and release finished Kitchen artifacts.</small></li>
          <li><span aria-hidden="true">02</span><strong>Manage</strong><small>Track recipes, packs, versions, and reviews.</small></li>
          <li><span aria-hidden="true">03</span><strong>Connect</strong><small>See plan, billing, and bound-server status.</small></li>
        </ul>
      </section>
      <section class="auth-panel">
        <span class="eyebrow">Secure account access</span>
        <h1>${isResetFlow ? 'Reset password' : isConfirmEmail ? 'Check your email' : 'Recued Dashboard'}</h1>
        <p class="auth-sub">${isResetFlow || isConfirmEmail ? '' : 'Sign in to your publisher workspace.'}</p>
        <div class="auth-card">${formBody}</div>
      </section>
    </main>
  `;
};

// ────────────────────────────────────────────────────────────────
// Recent activity + account-context rail (DB-6 / 14.3)
//
// The right rail anchors plan/binding context on the two account-home
// surfaces (Home + Account); the work consoles stay single-column. The
// Home recent-activity feed is synthesised client-side from the three
// already-loaded eager sources — submission lifecycle, pack publishes,
// and server-binding audit — so it costs no extra fetch. Billing
// transactions are lazy-loaded by design and are intentionally not pulled
// onto Home (documented slice-4 follow-up).
// ────────────────────────────────────────────────────────────────

const ACTIVITY_LIMIT = 8;

type ActivityTone = 'published' | 'pending' | 'rejected' | 'cancelled';

interface ActivityEvent {
  ts: number;
  title: string;
  detail: string;
  tone: ActivityTone;
}

/** Relative "Nm/Nh/Nd ago" label for the activity feed (client-side). */
const relativeTimeLabel = (ts: number): string => {
  const diff = Date.now() - ts;
  if (!Number.isFinite(diff) || diff < 60000) return 'just now';
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months}mo ago`;
  return `${Math.floor(months / 12)}y ago`;
};

const bindingActivityLabel = (action: AccountBindingAuditAction): { title: string; tone: ActivityTone } => {
  switch (action) {
    case 'account_bind': return { title: 'Server bound', tone: 'published' };
    case 'account_rebind': return { title: 'Server rebound to this account', tone: 'published' };
    case 'account_unbind': return { title: 'Server unbound', tone: 'cancelled' };
    case 'account_bind_conflict': return { title: 'Server binding conflict', tone: 'rejected' };
    case 'account_bind_exchange_failed': return { title: 'Server binding exchange failed', tone: 'rejected' };
    case 'credential_rotate': return { title: 'Server credential rotated', tone: 'pending' };
    default: return { title: 'Server binding event', tone: 'pending' };
  }
};

/** Merge the eager timestamped sources into one reverse-chronological feed. */
const buildRecentActivity = (state: DashboardState): ActivityEvent[] => {
  const events: ActivityEvent[] = [];

  // Submission lifecycle — submitted, then approved (=published) / rejected.
  for (const sub of state.submissions) {
    const name = sub.name || sub.slug;
    const typeLabel = sub.type === 'pack' ? 'pack' : 'recipe';
    const submittedTs = Date.parse(sub.submitted_at);
    if (Number.isFinite(submittedTs)) {
      events.push({ ts: submittedTs, title: `Submitted ${typeLabel} for review`, detail: name, tone: 'pending' });
    }
    if (sub.reviewed_at && (sub.status === 'approved' || sub.status === 'rejected')) {
      const reviewedTs = Date.parse(sub.reviewed_at);
      if (Number.isFinite(reviewedTs)) {
        const rejected = sub.status === 'rejected';
        events.push({
          ts: reviewedTs,
          title: rejected ? 'Review rejected' : `Published ${typeLabel}`,
          detail: name,
          tone: rejected ? 'rejected' : 'published',
        });
      }
    }
  }

  // Pack publishes — packs skip the review pipeline, so the pack row's own
  // timestamp is the publish signal (no matching submission row exists). Only
  // currently-published packs count: the items endpoint returns unpublished
  // packs too, and labelling those "Published pack" would be false. Timestamp
  // is `published_at` (the real publish time — DB-6a), falling back to the
  // always-present `created_at`; `updated_at` is deliberately NOT used so a
  // later meta-edit / status toggle doesn't masquerade as a fresh publish.
  for (const pack of state.myPacks) {
    if ((pack.status ?? 'published') !== 'published') continue;
    const ts = Date.parse(pack.published_at ?? pack.created_at ?? '');
    if (Number.isFinite(ts)) {
      events.push({ ts, title: 'Published pack', detail: pack.name ?? pack.slug ?? 'pack', tone: 'published' });
    }
  }

  // Server-binding lifecycle.
  for (const ev of state.accountBindingRead.auditEvents) {
    if (!Number.isFinite(ev.timestamp)) continue;
    const { title, tone } = bindingActivityLabel(ev.action);
    events.push({
      ts: ev.timestamp,
      title,
      detail: ev.server_fingerprint ? `Server ${ev.server_fingerprint.slice(0, 12)}` : '',
      tone,
    });
  }

  return events.sort((a, b) => b.ts - a.ts).slice(0, ACTIVITY_LIMIT);
};

const renderRecentActivity = (state: DashboardState): string => {
  const events = buildRecentActivity(state);
  return `
    <section class="activity-card">
      <h3>Recent activity</h3>
      ${events.length === 0 ? `
        <p class="activity-empty">No recent activity yet. Submissions, pack publishes, and server-binding events appear here.</p>
      ` : `
        <ul class="activity-feed">
          ${events.map((ev) => `
            <li class="activity-item">
              <span class="activity-dot tone-${ev.tone}" aria-hidden="true"></span>
              <div class="activity-body">
                <span class="activity-title">${e(ev.title)}</span>
                ${ev.detail ? `<span class="activity-detail">${e(ev.detail)}</span>` : ''}
              </div>
              <time class="activity-time" title="${e(formatTimestamp(ev.ts))}">${e(relativeTimeLabel(ev.ts))}</time>
            </li>
          `).join('')}
        </ul>
      `}
    </section>
  `;
};

/** The shared account-context rail — plan/binding anchor on Home + Account.
 *  Reads only eager state; complements (does not duplicate) the header chips
 *  with renewal/cancel detail, bound-server count, and the primary actions
 *  that are not already in the global-actions strip. */
const renderContextRail = (state: DashboardState): string => {
  const boundCount = state.accountBindingRead.bindings.length;
  const avatarLabel = (state.publisherHandle ?? state.user?.email ?? 'R').slice(0, 1).toUpperCase();
  const planNote = state.cancelScheduled && state.billingRenewalLabel
    ? `Cancels ${e(state.billingRenewalLabel)}`
    : state.billingRenewalLabel
      ? `Renews ${e(state.billingRenewalLabel)}`
      : '';
  const publisherUrl = state.publisherHandle ? publisherPageUrl(state.publisherHandle) : null;
  return `
    <aside class="context-rail" aria-label="Account context">
      <div class="rail-profile">
        <span class="rail-avatar" aria-hidden="true">${e(avatarLabel)}</span>
        <div>
          <span class="eyebrow">Account context</span>
          <strong class="rail-profile-name">${state.publisherHandle ? `@${e(state.publisherHandle)}` : e(state.user?.email ?? 'Recued publisher')}</strong>
        </div>
      </div>
      <div class="rail-row"><span>Publisher</span><strong>${state.publisherHandle ? `@${e(state.publisherHandle)}` : 'Not set'}</strong></div>
      <div class="rail-row"><span>Plan</span><span class="chip chip-plan chip-${chipClass(state.plan)}">${e(state.plan)}</span></div>
      ${planNote ? `<p class="rail-note">${planNote}</p>` : ''}
      <div class="rail-row"><span>Server binding</span><span class="chip chip-binding chip-${chipClass(state.bindingStatus)}">${e(state.bindingStatus)}</span></div>
      ${boundCount > 0 ? `<p class="rail-note">${boundCount} bound server${boundCount === 1 ? '' : 's'}</p>` : ''}
      <div class="rail-links">
        <a href="#/publish" class="btn btn-primary btn-sm">Publish new</a>
        ${publisherUrl ? `<a href="${e(publisherUrl)}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm">View publisher page</a>` : ''}
      </div>
    </aside>
  `;
};

// ────────────────────────────────────────────────────────────────
// Home
// ────────────────────────────────────────────────────────────────

const renderHome = (state: DashboardState): string => {
  // A brand-new account (no handle AND nothing published) gets a welcoming
  // onboarding + a soft empty panel, instead of a bare heading over three big
  // zeros. Once they claim a handle or publish anything, the normal cockpit
  // (stats + activity) takes over.
  const isFirstRun =
    !state.publisherHandle && state.myRecipes.length === 0 && state.myPacks.length === 0;
  const mainBody = isFirstRun
    ? `
      ${renderFirstRunOnboarding(state)}
      <div class="empty-panel">
        <p>Your published recipes, packs, and total installs will appear here once you publish.</p>
        <a href="#/publish" class="btn btn-primary btn-sm">Publish your first recipe</a>
      </div>`
    : `
      ${!state.publisherHandle ? renderPublisherSetup(state) : ''}
      <div class="stat-grid">
        <a class="stat-card" href="#/recipes">
          <div class="stat-label">Published recipes</div>
          <div class="stat-value">${state.myRecipes.length}</div>
          <span class="stat-link">Manage recipes <span aria-hidden="true">→</span></span>
        </a>
        <a class="stat-card" href="#/packs">
          <div class="stat-label">Published packs</div>
          <div class="stat-value">${state.myPacks.length}</div>
          <span class="stat-link">Manage packs <span aria-hidden="true">→</span></span>
        </a>
        <div class="stat-card">
          <div class="stat-label">Total installs</div>
          <div class="stat-value">${totalDownloads(state)}</div>
          <span class="stat-link stat-link-muted">Across your catalog</span>
        </div>
      </div>
      ${renderRecentActivity(state)}`;
  return `
  <section class="dash-page page-rail">
    <div class="page-rail-main">
      <div class="page-header page-header-home">
        <div>
          <span class="eyebrow">Publisher overview</span>
          <h2>Dashboard</h2>
          <p class="page-subtitle">Release finished work, watch its reach, and keep your account healthy.</p>
        </div>
      </div>
      ${mainBody}
    </div>
    ${renderContextRail(state)}
  </section>
`;
};

const totalDownloads = (state: DashboardState): number =>
  [...state.myRecipes, ...state.myPacks].reduce((sum, i) => {
    const downloads = typeof i.download_count === 'number'
      ? i.download_count
      : typeof i.downloads === 'number'
        ? i.downloads
        : 0;
    return sum + downloads;
  }, 0);

/** The publisher-handle claim form — shared by the compact setup card and the
 *  first-run onboarding so the wiring (`data-field` / `data-action`) stays in
 *  one place. */
const renderHandleClaimForm = (state: DashboardState, buttonLabel = 'Set handle'): string => `
  <div class="publisher-form">
    <input type="text" data-field="publisher-handle" value="${e(state.publisherInput)}"
      placeholder="my-handle" spellcheck="false" />
    <button type="button" class="btn btn-primary" data-action="create-publisher"
      ${!state.publisherInput.trim() ? 'disabled' : ''}>${e(buttonLabel)}</button>
  </div>
`;

const renderPublisherSetup = (state: DashboardState): string => `
  <div class="publisher-setup">
    <p>Set your publisher handle to start publishing.</p>
    ${renderHandleClaimForm(state)}
  </div>
`;

/** First-run welcome — the two things a new free account does: claim a
 *  publisher handle (to publish) and connect a self-hosted server (binding
 *  runs in the webclient). Numbered via a CSS counter on `.onboarding-steps`. */
const renderFirstRunOnboarding = (state: DashboardState): string => `
  <section class="onboarding-card">
    <span class="eyebrow">Get started</span>
    <h3 class="onboarding-title">Welcome to Recued</h3>
    <p class="onboarding-intro">Publish recipes and packs to the marketplace, and bind this account to your self-hosted server. Two quick steps:</p>
    <ol class="onboarding-steps">
      <li class="onboarding-step">
        <div class="onboarding-step-body">
          <strong>Claim your publisher handle</strong>
          <p class="onboarding-step-note">Your marketplace identity — everything you publish lives under it.</p>
          ${renderHandleClaimForm(state, 'Claim handle')}
        </div>
      </li>
      <li class="onboarding-step">
        <div class="onboarding-step-body">
          <strong>Connect your server</strong>
          <p class="onboarding-step-note">Open the Recued app and go to Settings → Account to bind this account to your self-hosted server.</p>
          <a href="${APP_URL}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm">Open the Recued app</a>
        </div>
      </li>
    </ol>
  </section>
`;

/** R20 — the publisher's marketplace-presentation profile (website + bio).
 *  Read view shows the current values + an Edit button; the editor PATCHes
 *  `/v1/dashboard/publisher`. `website` (https-only) feeds the marketplace
 *  About link; `bio` enriches the publisher page. */
/** AW-2 — the Handle row, with an inline rename editor. The handle is the
 *  publisher PK; renaming re-attributes every published artifact + moves the
 *  publisher page URL, so the editor states that plainly before the change. */
const renderHandleRow = (state: DashboardState): string => {
  if (!state.publisherHandle) {
    return `
      <div class="account-row">
        <span class="account-label">Handle</span>
        <span><em>Not set</em></span>
      </div>
    `;
  }
  if (state.handleRenameOpen) {
    const next = state.handleRenameInput.trim().toLowerCase();
    const unchanged = next === state.publisherHandle;
    return `
      <div class="meta-editor" data-handle-editor>
        <label class="form-label">
          <span>New handle (lowercase letters, digits, hyphens — starts with a letter)</span>
          <input type="text" data-field="handle-rename" value="${e(state.handleRenameInput)}"
            placeholder="my-handle" spellcheck="false" maxlength="40" autocomplete="off" />
        </label>
        <p class="account-hint account-warn">Changing your handle re-attributes every published recipe and pack and moves your publisher page to <code>/publishers/${e(next || 'new-handle')}</code> — the old <code>/publishers/${e(state.publisherHandle)}</code> link stops working. Individual recipe and pack URLs are unaffected.</p>
        ${state.handleRenameMessage ? `<p class="account-hint">${e(state.handleRenameMessage)}</p>` : ''}
        <div class="row-actions">
          <button type="button" class="btn btn-primary btn-sm" data-action="save-handle"
            ${state.handleRenameSaving || !next || unchanged ? 'disabled' : ''}>${state.handleRenameSaving ? 'Changing…' : 'Change handle'}</button>
          <button type="button" class="btn btn-secondary btn-sm" data-action="cancel-handle"
            ${state.handleRenameSaving ? 'disabled' : ''}>Cancel</button>
        </div>
      </div>
    `;
  }
  return `
    <div class="account-row">
      <span class="account-label">Handle</span>
      <span class="handle-value"><code>${e(state.publisherHandle)}</code>
        <button type="button" class="btn btn-secondary btn-sm" data-action="edit-handle">Change</button></span>
    </div>
  `;
};

const renderPublisherProfile = (state: DashboardState): string => {
  const websiteOk = typeof state.publisherWebsite === 'string' && state.publisherWebsite.startsWith('https://');
  if (state.profileEditing) {
    return `
      <div class="meta-editor" data-profile-editor>
        ${state.profileMessage ? `<p class="account-hint">${e(state.profileMessage)}</p>` : ''}
        <label class="form-label">
          <span>Website (https URL — shown on your marketplace listings; leave empty to clear)</span>
          <input type="url" data-field="edit-website" value="${e(state.profileWebsiteInput)}"
            placeholder="https://example.com" maxlength="300" />
        </label>
        <label class="form-label">
          <span>Bio (shown on your publisher page; leave empty to clear)</span>
          <textarea data-field="edit-bio" maxlength="500"
            placeholder="A short description of who you are.">${e(state.profileBioInput)}</textarea>
        </label>
        <div class="row-actions">
          <button type="button" class="btn btn-primary btn-sm" data-action="save-publisher-profile"
            ${state.profileSaving ? 'disabled' : ''}>${state.profileSaving ? 'Saving…' : 'Save profile'}</button>
          <button type="button" class="btn btn-secondary btn-sm" data-action="cancel-profile-edit"
            ${state.profileSaving ? 'disabled' : ''}>Cancel</button>
        </div>
      </div>
    `;
  }
  return `
    <div class="account-row">
      <span class="account-label">Website</span>
      <span>${websiteOk
        ? `<a href="${e(state.publisherWebsite!)}" target="_blank" rel="noopener">${e(state.publisherWebsite!)}</a>`
        : '<em>Not set</em>'}</span>
    </div>
    <div class="account-row">
      <span class="account-label">Bio</span>
      <span>${state.publisherBio ? e(state.publisherBio) : '<em>Not set</em>'}</span>
    </div>
    ${state.profileMessage ? `<p class="account-hint">${e(state.profileMessage)}</p>` : ''}
    <div class="row-actions">
      <button type="button" class="btn btn-secondary btn-sm" data-action="edit-profile">Edit profile</button>
    </div>
  `;
};

// ────────────────────────────────────────────────────────────────
// Recipes / packs lists
// ────────────────────────────────────────────────────────────────

interface RecipeConsoleRow {
  key: string;
  source: 'published' | 'submission';
  recipeId: string;
  slug: string;
  canonicalSlug: string;
  marketplaceUrl: string;
  name: string;
  description: string;
  version: number;
  status: Exclude<RecipeStatusFilter, 'all'>;
  downloads: number;
  ratingLabel: string;
  executionScope: string;
  updatedAt: string | null;
  tags: string[];
  platforms: string[];
  dependencyCount: number;
  provenanceLabel: string;
  forkLineage: string | null;
  submittedAt: string | null;
  reviewedAt: string | null;
  versionRecords: Record<string, unknown>[];
  submission: SubmissionItem | null;
  manifest: Record<string, unknown>;
}

const RECIPE_STATUS_FILTERS: Array<[RecipeStatusFilter, string]> = [
  ['all', 'All status'],
  ['published', 'Published'],
  ['pending', 'Pending'],
  ['approved', 'Approved'],
  ['rejected', 'Rejected'],
  ['cancelled', 'Cancelled'],
  ['unpublished', 'Unpublished'],
];

const RECIPE_SORTS: Array<[RecipeSort, string]> = [
  ['updated-desc', 'Recently updated'],
  ['name-asc', 'Name'],
  ['status-asc', 'Status'],
];

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const recordAt = (value: unknown): Record<string, unknown> =>
  isRecord(value) ? value : {};

const firstString = (...values: unknown[]): string | null => {
  for (const value of values) {
    if (typeof value === 'string' && value.trim()) return value.trim();
    if (typeof value === 'number' && Number.isFinite(value)) return String(value);
  }
  return null;
};

const firstNumber = (...values: unknown[]): number | null => {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return value;
    if (typeof value === 'string' && value.trim()) {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
};

const stringArray = (value: unknown): string[] =>
  Array.isArray(value)
    ? value.filter((entry): entry is string => typeof entry === 'string' && entry.trim().length > 0)
    : [];

const recordArray = (value: unknown): Record<string, unknown>[] =>
  Array.isArray(value) ? value.filter(isRecord) : [];

const itemPayload = (item: PublishedItem): Record<string, unknown> =>
  recordAt(item.recipe ?? item.manifest);

const itemMeta = (payload: Record<string, unknown>): Record<string, unknown> =>
  recordAt(payload.metadata);

const canonicalSlug = (publisher: string | null | undefined, slug: string): string => {
  if (!slug) return publisher ?? '';
  if (slug.includes('/')) return slug;
  return publisher ? `${publisher}/${slug}` : slug;
};

const marketplaceDetailUrl = (kind: 'recipe' | 'pack', id: string): string =>
  `${MARKETPLACE_URL}/${kind === 'recipe' ? 'recipes' : 'packs'}/${encodeURIComponent(id)}`;

const formatDateLabel = (value: string | null | undefined): string => {
  if (!value) return 'Not set';
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return value;
  return `${new Date(time).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

/** Short calendar date for scannable console cells ("Jul 8, 2026"). The
 *  precise `formatDateLabel` stamp belongs in the cell's title attribute;
 *  audit/history surfaces keep the full stamp inline. */
const formatShortDate = (value: string | null | undefined): string => {
  if (!value) return 'Not set';
  const time = Date.parse(value);
  if (!Number.isFinite(time)) return value;
  return new Date(time).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

const normalizeSlugReservationState = (value: unknown): SlugReservationState | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.toLowerCase().replace(/[\s_]+/g, '-');
  if (normalized === 'available') return 'available';
  if (normalized === 'owned-by-this-account' || normalized === 'owned') return 'owned-by-this-account';
  if (normalized === 'reserved-pending' || normalized === 'pending' || normalized === 'pending-review') return 'reserved-pending';
  if (normalized === 'unavailable' || normalized === 'taken') return 'unavailable';
  if (normalized === 'expiring' || normalized === 'expires-soon') return 'expiring';
  return null;
};

const slugReservationLabel = (state: SlugReservationState | null): string => {
  switch (state) {
    case 'available': return 'Available';
    case 'owned-by-this-account': return 'Owned by this account';
    case 'reserved-pending': return 'Reserved pending';
    case 'unavailable': return 'Unavailable';
    case 'expiring': return 'Expiring';
    default: return 'Pending';
  }
};

const slugReservationDetail = (state: SlugReservationState | null, slug: string | null): string => {
  const displaySlug = slug ?? 'this slug';
  switch (state) {
    case 'available':
      return `${displaySlug} is not reserved by another visible submission.`;
    case 'owned-by-this-account':
      return `${displaySlug} belongs to this publisher account; publishing creates a new version.`;
    case 'reserved-pending':
      return `${displaySlug} already has a pending submission; track it in the In-review tab.`;
    case 'unavailable':
      return `${displaySlug} is not available for this publisher namespace.`;
    case 'expiring':
      return `${displaySlug} has an expiring reservation; it is re-checked when you publish.`;
    default:
      return 'The slug is reserved in your publisher namespace when you publish.';
  }
};

const slugMatches = (publisherHandle: string | null, candidate: string, target: string): boolean => {
  const candidateCanonical = canonicalSlug(publisherHandle, candidate);
  const targetCanonical = canonicalSlug(publisherHandle, target);
  return candidate === target
    || candidateCanonical === targetCanonical
    || candidateCanonical.endsWith(`/${target}`)
    || targetCanonical.endsWith(`/${candidate}`);
};

const isNearExpiry = (expiresAt: string | null | undefined, now: number): boolean => {
  if (!expiresAt) return false;
  const expires = Date.parse(expiresAt);
  if (!Number.isFinite(expires)) return false;
  return expires <= now + 7 * 24 * 60 * 60 * 1000;
};

const slugReservationStateForSubmission = (
  submission: SubmissionItem,
  now = Date.now(),
): SlugReservationState => {
  const explicit = normalizeSlugReservationState(submission.slug_reservation_state);
  if (explicit) return explicit;
  if (submission.status === 'approved') return 'owned-by-this-account';
  if (submission.status === 'pending') return 'reserved-pending';
  if (submission.status === 'cancelled') return 'available';
  if (isNearExpiry(submission.expires_at, now)) return 'expiring';
  return 'unavailable';
};

const itemSlugReservationState = (item: PublishedItem): SlugReservationState =>
  normalizeSlugReservationState(item.slug_reservation_state) ?? 'owned-by-this-account';

export const canCancelSubmission = (submission: SubmissionItem, now = Date.now()): boolean => {
  if (submission.status !== 'pending') return false;
  if (submission.cancellation_allowed === false) return false;
  if (!submission.cancelable_until) return true;
  const cancelableUntil = Date.parse(submission.cancelable_until);
  return Number.isFinite(cancelableUntil) && cancelableUntil >= now;
};

export const markSubmissionCancelled = (
  submissions: SubmissionItem[],
  reviewId: string,
  cancelledAt = new Date().toISOString(),
): SubmissionItem[] =>
  submissions.map((submission) => submission.review_id === reviewId
    ? {
        ...submission,
        status: 'cancelled',
        reviewed_at: submission.reviewed_at ?? cancelledAt,
        expires_at: null,
        slug_reservation_state: 'available',
      }
    : submission);

const normalizeRecipeStatus = (value: unknown, fallback: Exclude<RecipeStatusFilter, 'all'>): Exclude<RecipeStatusFilter, 'all'> => {
  if (typeof value !== 'string') return fallback;
  const normalized = value.toLowerCase().replace(/[\s-]+/g, '_');
  if (normalized === 'published') return 'published';
  if (normalized === 'approved') return 'approved';
  if (normalized === 'pending' || normalized === 'review') return 'pending';
  if (normalized === 'rejected') return 'rejected';
  if (normalized === 'cancelled' || normalized === 'canceled') return 'cancelled';
  if (normalized === 'unpublished') return 'unpublished';
  return fallback;
};

const statusLabel = (value: Exclude<RecipeStatusFilter, 'all'> | SubmissionStatus): string => {
  switch (value) {
    case 'published': return 'Published';
    case 'pending': return 'Pending review';
    case 'approved': return 'Approved';
    case 'rejected': return 'Rejected';
    case 'cancelled': return 'Cancelled';
    case 'unpublished': return 'Unpublished';
  }
};

const collectDependencySlugs = (payload: Record<string, unknown>): string[] => {
  const slugs = new Set<string>();
  const collect = (value: unknown): void => {
    if (!Array.isArray(value)) return;
    for (const entry of value) {
      const step = recordAt(entry);
      const ingredient = firstString(step.ingredient);
      if (ingredient) slugs.add(ingredient);
    }
  };
  collect(payload.prefetch_steps);
  collect(payload.steps);
  return [...slugs].sort((a, b) => a.localeCompare(b));
};

const collectDependencyCount = (payload: Record<string, unknown>): number =>
  collectDependencySlugs(payload).length;

const stringListLabel = (value: unknown): string | null => {
  const values = stringArray(value);
  return values.length > 0 ? values.join(', ') : firstString(value);
};

const executionScopeLabel = (payload: Record<string, unknown>, meta: Record<string, unknown>): string =>
  stringListLabel(meta.execution_scope)
  ?? stringListLabel(payload.execution_scope)
  ?? firstString(meta.runtime, payload.runtime)
  ?? 'Local';

const provenanceLabel = (payload: Record<string, unknown>, meta: Record<string, unknown>): string => {
  const value = meta.provenance ?? payload.provenance;
  return value === false ? 'Provenance opt-out' : 'Provenance tracked';
};

const forkLineageLabel = (...values: unknown[]): string | null => {
  for (const value of values) {
    const direct = firstString(value);
    if (direct) return direct;
    const record = recordAt(value);
    const nested = firstString(record.slug, record.recipe_id, record.ingredient_id, record.publisher_slug, record.source_slug);
    if (nested) return nested;
  }
  return null;
};

const buildRecipeRows = (state: DashboardState): RecipeConsoleRow[] => {
  const publisher = state.publisherHandle ?? '';
  const rows: RecipeConsoleRow[] = state.myRecipes.map((item, index) => {
    const payload = itemPayload(item);
    const meta = itemMeta(payload);
    const recipeId = firstString(item.recipe_id, payload.recipe_id, item.slug, meta.recipe_id) ?? `recipe-${index + 1}`;
    const slug = firstString(item.slug, item.recipe_id, payload.recipe_id, meta.slug) ?? recipeId;
    const name = firstString(item.name, meta.name, payload.name, slug) ?? slug;
    const version = firstNumber(item.version, payload.version) ?? 1;
    const downloads = firstNumber(item.download_count, item.downloads) ?? 0;
    const ratingCount = firstNumber(item.rating_count) ?? 0;
    const ratingAvg = firstNumber(item.rating_avg) ?? 0;
    const canonical = canonicalSlug(item.publisher_id || publisher, slug);
    return {
      key: `published:${item.publisher_id}:${recipeId}`,
      source: 'published',
      recipeId,
      slug,
      canonicalSlug: canonical,
      marketplaceUrl: marketplaceDetailUrl('recipe', recipeId),
      name,
      description: firstString(item.description, meta.description, payload.description) ?? '',
      version,
      status: normalizeRecipeStatus(item.status, 'published'),
      downloads,
      ratingLabel: ratingCount > 0 ? `${ratingAvg.toFixed(1)} (${ratingCount})` : 'No ratings',
      executionScope: executionScopeLabel(payload, meta),
      updatedAt: firstString(item.updated_at, item.created_at) ?? null,
      tags: stringArray(item.tags).length > 0 ? stringArray(item.tags) : stringArray(meta.tags),
      platforms: stringArray(item.platforms).length > 0 ? stringArray(item.platforms) : stringArray(meta.supported_platforms),
      dependencyCount: collectDependencyCount(payload),
      provenanceLabel: provenanceLabel(payload, meta),
      forkLineage: forkLineageLabel(item.fork_of, payload.fork_of, meta.fork_of),
      submittedAt: firstString(item.submitted_at, payload.submitted_at, meta.submitted_at),
      reviewedAt: firstString(item.reviewed_at, item.approved_at, payload.reviewed_at, payload.approved_at, meta.reviewed_at, meta.approved_at),
      versionRecords: recordArray(item.version_history ?? payload.version_history ?? meta.version_history),
      submission: null,
      manifest: payload,
    };
  });

  const publishedKeys = new Set(rows.flatMap((row) => [row.canonicalSlug, row.recipeId, row.slug]));
  const submissionRows = state.submissions
    .filter((submission) => submission.type === 'recipe')
    .filter((submission) => submission.status !== 'approved' || !publishedKeys.has(canonicalSlug(publisher, submission.slug)))
    .map((submission): RecipeConsoleRow => {
      const canonical = canonicalSlug(publisher, submission.slug);
      return {
        key: `submission:${submission.review_id}`,
        source: 'submission',
        recipeId: canonical,
        slug: submission.slug,
        canonicalSlug: canonical,
        marketplaceUrl: marketplaceDetailUrl('recipe', canonical),
        name: submission.name || submission.slug,
        description: '',
        version: submission.version,
        status: submission.status,
        downloads: 0,
        ratingLabel: 'No ratings',
        executionScope: 'Local',
        updatedAt: submission.reviewed_at ?? submission.submitted_at,
        tags: [],
        platforms: [],
        dependencyCount: 0,
        provenanceLabel: 'Review snapshot',
        forkLineage: forkLineageLabel(submission.fork_of, submission.source_slug),
        submittedAt: submission.submitted_at,
        reviewedAt: submission.reviewed_at,
        versionRecords: [],
        submission,
        manifest: {},
      };
    });

  return [...rows, ...submissionRows];
};

const filterRecipeRows = (state: DashboardState, rows: RecipeConsoleRow[]): RecipeConsoleRow[] => {
  const query = state.recipeSearch.trim().toLowerCase();
  return rows.filter((row) => {
    if (state.recipeStatusFilter !== 'all' && row.status !== state.recipeStatusFilter) return false;
    if (!query) return true;
    return [
      row.name,
      row.slug,
      row.canonicalSlug,
      row.description,
      ...row.tags,
      ...row.platforms,
    ].some((value) => value.toLowerCase().includes(query));
  });
};

const sortRecipeRows = (rows: RecipeConsoleRow[], sort: RecipeSort): RecipeConsoleRow[] => {
  const copy = [...rows];
  if (sort === 'name-asc') return copy.sort((a, b) => a.name.localeCompare(b.name));
  if (sort === 'status-asc') return copy.sort((a, b) => statusLabel(a.status).localeCompare(statusLabel(b.status)));
  return copy.sort((a, b) => (Date.parse(b.updatedAt ?? '') || 0) - (Date.parse(a.updatedAt ?? '') || 0));
};

/** Unpublish / republish control for a published artifact row. `id` is
 *  the worker key (`recipe_id` for recipes, `slug` for packs). Rendered
 *  only for rows that exist in the published tables — submission-only
 *  rows have nothing to flip. */
export const renderStatusToggle = (
  state: DashboardState,
  type: 'recipe' | 'pack',
  id: string,
  status: string,
): string => {
  const key = `${type}:${id}`;
  const busy = state.itemStatusUpdatingKey === key;
  // Any in-flight mutation disables every toggle (the handler runs one
  // PATCH at a time); only the active row renders the busy label.
  const locked = state.itemStatusUpdatingKey !== null;
  const error = state.itemStatusErrors[key];
  const toUnpublished = status !== 'unpublished';
  const label = busy
    ? (toUnpublished ? 'Unpublishing' : 'Republishing')
    : (toUnpublished ? 'Unpublish' : 'Republish');
  return `
    <button type="button" class="btn btn-secondary btn-sm ${toUnpublished ? 'danger-action' : ''}"
      data-action="${toUnpublished ? 'unpublish-item' : 'republish-item'}"
      data-item-type="${e(type)}" data-item-id="${e(id)}"
      ${busy ? 'disabled aria-busy="true"' : locked ? 'disabled' : ''}
      title="${toUnpublished
        ? 'Remove from the marketplace listing; installs keep working and you can republish any time'
        : 'Restore to the marketplace listing'}">${label}</button>
    ${error ? `<span class="row-error">${e(error)}</span>` : ''}
  `;
};

export const META_DESCRIPTION_MAX = 500;
export const META_REPO_URL_MAX = 300;

/** "Edit details" affordance — opens the metadata editor prefilled with
 *  the current repo URL + description (carried on the dataset so the
 *  handler needs no row lookup). */
export const renderMetaEditButton = (
  type: 'recipe' | 'pack',
  id: string,
  repo: string,
  description: string,
): string => `
  <button type="button" class="btn btn-secondary btn-sm"
    data-action="edit-item-meta"
    data-item-type="${e(type)}" data-item-id="${e(id)}"
    data-repo="${e(repo)}" data-description="${e(description)}">Edit details</button>
`;

/** Metadata editor — support-repo URL + marketplace description. The
 *  worker sanitizes at the storage boundary (HTML stripped, length
 *  clamped); the maxlength attributes are first-line UX only. */
export const renderMetaEditor = (
  state: DashboardState,
  type: 'recipe' | 'pack',
  id: string,
): string => {
  const key = `${type}:${id}`;
  if (state.editingItemKey !== key) return '';
  return `
    <div class="meta-editor" data-meta-editor="${e(key)}">
      <label>
        <span>Support repo (https URL — issues &amp; support route there; leave empty to clear)</span>
        <input type="url" data-field="edit-repo" value="${e(state.editRepoInput)}"
          maxlength="${META_REPO_URL_MAX}" placeholder="https://github.com/you/your-repo" />
      </label>
      <label>
        <span>Marketplace description</span>
        <textarea data-field="edit-description" rows="3"
          maxlength="${META_DESCRIPTION_MAX}">${e(state.editDescriptionInput)}</textarea>
      </label>
      ${state.metaError ? `<div class="row-error">${e(state.metaError)}</div>` : ''}
      <div class="row-actions">
        <button type="button" class="btn btn-primary btn-sm" data-action="save-item-meta"
          ${state.metaSaving ? 'disabled aria-busy="true"' : ''}>${state.metaSaving ? 'Saving' : 'Save details'}</button>
        <button type="button" class="btn btn-secondary btn-sm" data-action="cancel-item-meta"
          ${state.metaSaving ? 'disabled' : ''}>Cancel</button>
      </div>
    </div>
  `;
};

/** Reviewer rejection reason(s) block — shown in the recipes-console drawer
 *  for a rejected submission record. */
const renderRejectionReasons = (submission: SubmissionItem): string => {
  const reasons = submission.rejection_reasons?.length
    ? submission.rejection_reasons
    : submission.rejection_reason
      ? [submission.rejection_reason]
      : [];
  if (reasons.length === 0) return '';
  return `
    <div class="review-reason">
      <strong>Rejection reason${reasons.length > 1 ? 's' : ''}</strong>
      ${reasons.map((reason) => `<p>${e(reason)}</p>`).join('')}
      <span>${e(formatDateLabel(submission.rejection_reason_at ?? submission.reviewed_at))}</span>
    </div>
  `;
};

const renderRecipeRowActions = (state: DashboardState, row: RecipeConsoleRow): string => {
  const canOpenMarketplace = row.status === 'published' || row.status === 'approved';
  const pendingCancel = row.submission?.status === 'pending';
  const cancelAllowed = pendingCancel && canCancelSubmission(row.submission!);
  const cancelling = cancelAllowed && state.submissionCancellingId === row.submission?.review_id;
  return `
    <div class="row-actions">
      ${canOpenMarketplace
        ? `<a href="${e(row.marketplaceUrl)}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm">View</a>`
        : '<button type="button" class="btn btn-secondary btn-sm" disabled>View</button>'}
      <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm" data-action="open-kitchen">New version</a>
      <button type="button" class="btn btn-secondary btn-sm" data-action="copy-text"
        data-copy-value="${e(row.recipeId)}" data-copy-label="Recipe ID">Copy ID</button>
      ${pendingCancel ? cancelAllowed ? `
        <button type="button" class="btn btn-secondary btn-sm danger-action"
          data-action="cancel-submission" data-review-id="${e(row.submission!.review_id)}"
          ${cancelling ? 'disabled aria-busy="true"' : ''}>${cancelling ? 'Cancelling' : 'Cancel pending'}</button>
      ` : '<button type="button" class="btn btn-secondary btn-sm" disabled title="Cancel window closed">Cancel window closed</button>' : ''}
      ${row.source === 'published' ? renderStatusToggle(state, 'recipe', row.recipeId, row.status) : ''}
    </div>
  `;
};

interface VersionHistoryEntry {
  version: number;
  status: Exclude<RecipeStatusFilter, 'all'>;
  submittedAt: string | null;
  reviewedAt: string | null;
  current: boolean;
  forkLineage: string | null;
}

const versionHistoryFromRecords = (
  records: Record<string, unknown>[],
  currentVersion: number,
  fallbackStatus: Exclude<RecipeStatusFilter, 'all'>,
): VersionHistoryEntry[] =>
  records
    .map((record): VersionHistoryEntry | null => {
      const version = firstNumber(record.version, record.version_number);
      if (!version) return null;
      return {
        version,
        status: normalizeRecipeStatus(record.status, fallbackStatus),
        submittedAt: firstString(record.submitted_at, record.submittedAt),
        reviewedAt: firstString(record.reviewed_at, record.reviewedAt, record.approved_at, record.approvedAt),
        current: Boolean(record.current) || version === currentVersion,
        forkLineage: forkLineageLabel(record.fork_of, record.source_slug),
      };
    })
    .filter((entry): entry is VersionHistoryEntry => entry !== null);

const renderVersionHistoryEntries = (
  entries: VersionHistoryEntry[],
  currentVersion: number,
  currentStatus: Exclude<RecipeStatusFilter, 'all'>,
  fallbackSubmittedAt: string | null,
  fallbackReviewedAt: string | null,
  fallbackForkLineage: string | null,
): string => {
  const keyed = new Map<string, VersionHistoryEntry>();
  for (const entry of entries) {
    keyed.set(`${entry.version}:${entry.status}:${entry.submittedAt ?? ''}:${entry.reviewedAt ?? ''}`, entry);
  }
  const hasCurrent = [...keyed.values()].some((entry) => entry.current || entry.version === currentVersion);
  if (!hasCurrent) {
    keyed.set(`current:${currentVersion}`, {
      version: currentVersion,
      status: currentStatus,
      submittedAt: fallbackSubmittedAt,
      reviewedAt: fallbackReviewedAt,
      current: true,
      forkLineage: fallbackForkLineage,
    });
  }
  const sorted = [...keyed.values()]
    .map((entry) => ({ ...entry, current: entry.current || entry.version === currentVersion }))
    .sort((a, b) => {
      if (a.current !== b.current) return a.current ? -1 : 1;
      return b.version - a.version;
    });

  return `
    <ol>
      ${sorted.map((entry) => `
        <li>
          <span>${entry.current ? 'Current' : 'Prior'} v${entry.version} ${e(statusLabel(entry.status))}</span>
          <time>Submitted: ${e(formatDateLabel(entry.submittedAt))}</time>
          <time>Reviewed: ${e(formatDateLabel(entry.reviewedAt))}</time>
          ${entry.forkLineage ? `<time>Fork lineage: ${e(entry.forkLineage)}</time>` : ''}
        </li>
      `).join('')}
    </ol>
  `;
};

const versionHistoryFor = (state: DashboardState, row: RecipeConsoleRow): VersionHistoryEntry[] => {
  const submissionEntries = state.submissions
    .filter((submission) => submission.type === 'recipe')
    .filter((submission) => slugMatches(state.publisherHandle, submission.slug, row.slug)
      || canonicalSlug(state.publisherHandle, submission.slug) === row.canonicalSlug)
    .map((submission): VersionHistoryEntry => ({
      version: submission.version,
      status: submission.status,
      submittedAt: submission.submitted_at,
      reviewedAt: submission.reviewed_at,
      current: submission.version === row.version && submission.status === row.status,
      forkLineage: forkLineageLabel(submission.fork_of, submission.source_slug),
    }));
  return [
    ...versionHistoryFromRecords(row.versionRecords, row.version, row.status),
    ...submissionEntries,
  ].sort((a, b) => (Date.parse(b.submittedAt ?? '') || 0) - (Date.parse(a.submittedAt ?? '') || 0));
};

const renderRecipeDrawer = (state: DashboardState, row: RecipeConsoleRow): string => {
  const history = versionHistoryFor(state, row);
  return `
    <aside class="detail-drawer" data-recipe-drawer>
      <div class="drawer-header">
        <span class="eyebrow">Marketplace preview</span>
        <h3>${e(row.name)}</h3>
        <span class="chip chip-${chipClass(row.status)}">${e(statusLabel(row.status))}</span>
      </div>
      <div class="drawer-row"><span>Recipe ID</span><code>${e(row.recipeId)}</code></div>
      <div class="drawer-row"><span>Slug</span><code>${e(row.canonicalSlug)}</code></div>
      <div class="drawer-row"><span>Version</span><strong>v${e(String(row.version))}</strong></div>
      <div class="drawer-row"><span>Installs</span><strong>${e(String(row.downloads))}</strong></div>
      <div class="drawer-row"><span>Rating</span><strong>${e(row.ratingLabel)}</strong></div>
      <div class="drawer-row"><span>Execution scope</span><strong>${e(row.executionScope)}</strong></div>
      <div class="drawer-row"><span>Dependencies</span><strong>${row.dependencyCount}</strong></div>
      <div class="drawer-row"><span>Provenance</span><strong>${e(row.provenanceLabel)}</strong></div>
      <div class="drawer-row"><span>Fork lineage</span><strong>${row.forkLineage ? e(row.forkLineage) : 'None declared'}</strong></div>
      <p class="drawer-desc">${row.description ? e(row.description) : 'No marketplace description is available in this dashboard projection.'}</p>
      ${row.tags.length > 0 || row.platforms.length > 0 ? `
        <div class="tag-row">
          ${[...row.tags, ...row.platforms].map((tag) => `<span class="tag-pill">${e(tag)}</span>`).join('')}
        </div>
      ` : ''}
      ${row.submission ? renderRejectionReasons(row.submission) : ''}
      ${row.submission && state.submissionActionErrors[row.submission.review_id] ? `
        <div class="sub-reason">${e(state.submissionActionErrors[row.submission.review_id]!)}</div>
      ` : ''}
      <div class="drawer-actions">
        ${row.status === 'published' || row.status === 'approved'
          ? `<a href="${e(row.marketplaceUrl)}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm">Marketplace detail</a>`
          : '<button type="button" class="btn btn-secondary btn-sm" disabled>Marketplace pending</button>'}
        <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" data-action="open-kitchen">Edit in Kitchen</a>
        <a href="#/submissions" class="btn btn-secondary btn-sm">Review status</a>
        ${row.source === 'published'
          ? renderMetaEditButton('recipe', row.recipeId,
              firstString(itemMeta(row.manifest).repo) ?? '', row.description)
          : ''}
      </div>
      ${row.source === 'published' ? renderMetaEditor(state, 'recipe', row.recipeId) : ''}
      <div class="version-history">
        <h4>Version history</h4>
        ${renderVersionHistoryEntries(history, row.version, row.status, row.submittedAt, row.reviewedAt ?? row.updatedAt, row.forkLineage)}
      </div>
    </aside>
  `;
};

const renderConsoleSkeleton = (label: string): string => `
  <div class="console-table skeleton-console" role="table" aria-label="${e(label)}" aria-busy="true">
    ${[1, 2, 3].map(() => `
      <div class="console-row" role="row">
        <span class="skeleton-line skeleton-wide" role="cell"></span>
        <span class="skeleton-line" role="cell"></span>
        <span class="skeleton-line" role="cell"></span>
      </div>
    `).join('')}
  </div>
`;

/** Minimal line icons (stroke = currentColor, so they theme + tint) for the
 *  free-path console empty states. */
const EMPTY_STATE_ICONS = {
  recipes:
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M6 3h9l4 4v14H6z"/><path d="M15 3v4h4"/><path d="M9 12h6M9 16h4"/></svg>',
  packs:
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M12 3l8 4.5v9L12 21l-8-4.5v-9z"/><path d="M4 7.5l8 4.5 8-4.5M12 12v9"/></svg>',
  submissions:
    '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M3 13l3-8h12l3 8v6H3z"/><path d="M3 13h5l1 2h4l1-2h5"/></svg>',
} as const;

/** A consistent, inviting empty state for the free-path consoles when a new
 *  account has nothing yet — centered badge + title + one line of guidance +
 *  the next-step actions (passed as trusted button markup). */
const renderEmptyState = (icon: string, title: string, body: string, actions: string): string => `
        <div class="empty-state">
          <div class="empty-state-badge" aria-hidden="true">${icon}</div>
          <h3 class="empty-state-title">${e(title)}</h3>
          <p class="empty-state-body">${e(body)}</p>
          ${actions ? `<div class="empty-state-actions">${actions}</div>` : ''}
        </div>`;

const renderMyRecipes = (state: DashboardState): string => {
  const allRows = buildRecipeRows(state);
  const filteredRows = sortRecipeRows(filterRecipeRows(state, allRows), state.recipeSort);
  const selected = filteredRows.find((row) => row.key === state.selectedRecipeKey) ?? filteredRows[0] ?? null;
  const hasPendingOnly = state.myRecipes.length === 0 && state.submissions.some((submission) => submission.type === 'recipe' && submission.status === 'pending');
  const hasFilters = state.recipeSearch.trim() || state.recipeStatusFilter !== 'all';

  return `
    <section class="dash-page console-page">
      <div class="page-header">
        <div>
          <h2>Recipes</h2>
          <p class="page-subtitle">Manage published recipes and review records. Edits and new versions open Kitchen.</p>
        </div>
        <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" data-action="open-kitchen">Open Kitchen</a>
      </div>

      <div class="toolbar" role="search">
        <label>
          <span>Search</span>
          <input type="search" data-field="recipe-search" value="${e(state.recipeSearch)}" placeholder="Name, slug, tag" />
        </label>
        <label>
          <span>Status</span>
          <select data-field="recipe-status-filter">
            ${RECIPE_STATUS_FILTERS.map(([value, label]) =>
              `<option value="${value}" ${state.recipeStatusFilter === value ? 'selected' : ''}>${label}</option>`).join('')}
          </select>
        </label>
        <label>
          <span>Sort</span>
          <select data-field="recipe-sort">
            ${RECIPE_SORTS.map(([value, label]) =>
              `<option value="${value}" ${state.recipeSort === value ? 'selected' : ''}>${label}</option>`).join('')}
          </select>
        </label>
        ${hasFilters ? '<button type="button" class="btn btn-secondary btn-sm" data-action="clear-recipe-filters">Clear filters</button>' : ''}
      </div>

      ${hasPendingOnly ? `
        <div class="empty-panel pending-panel">
          <p>No approved recipes yet. Pending records are tracked in Publish &rarr; In review.</p>
          <a href="#/submissions" class="btn btn-secondary btn-sm">View review status</a>
        </div>
      ` : ''}

      ${state.loading ? renderConsoleSkeleton('Loading recipes') : ''}
      ${allRows.length === 0 && !state.loading ? renderEmptyState(
        EMPTY_STATE_ICONS.recipes,
        'No recipes published yet',
        'Author recipes locally in Kitchen, then submit them here to publish to the marketplace.',
        `<a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" data-action="open-kitchen">Open Kitchen</a><a href="${MARKETPLACE_URL}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm">Browse marketplace</a>`,
      ) : ''}
      ${allRows.length > 0 && filteredRows.length === 0 ? `
        <div class="empty-panel">
          <p>No recipes match the current filters.</p>
          <button type="button" class="btn btn-secondary btn-sm" data-action="clear-recipe-filters">Clear filters</button>
        </div>
      ` : ''}
      ${filteredRows.length > 0 ? `
        <div class="console-grid">
          <div class="console-table recipe-console-table" role="table" aria-label="Publisher recipes">
            <div class="console-row console-head recipe-console-row" role="row">
              <span role="columnheader">Name</span>
              <span role="columnheader">Slug</span>
              <span role="columnheader">Version</span>
              <span role="columnheader">Status</span>
              <span role="columnheader">Installs</span>
              <span role="columnheader">Rating</span>
              <span role="columnheader">Scope</span>
              <span role="columnheader">Updated</span>
              <span role="columnheader">Actions</span>
            </div>
            ${filteredRows.map((row) => `
              <div class="console-row recipe-console-row ${selected?.key === row.key ? 'selected' : ''}" role="row">
                <button type="button" class="row-title" role="cell" data-action="select-recipe" data-recipe-key="${e(row.key)}">${e(row.name)}</button>
                <code role="cell" data-label="Slug">${e(row.canonicalSlug)}</code>
                <span role="cell" data-label="Version">v${row.version}</span>
                <span role="cell" data-label="Status"><span class="chip chip-${chipClass(row.status)}">${e(statusLabel(row.status))}</span></span>
                <span role="cell" data-label="Installs">${row.downloads}</span>
                <span role="cell" data-label="Rating">${e(row.ratingLabel)}</span>
                <span role="cell" data-label="Scope">${e(row.executionScope)}</span>
                <span role="cell" data-label="Updated" title="${e(formatDateLabel(row.updatedAt))}">${e(formatShortDate(row.updatedAt))}</span>
                <span role="cell" data-label="Actions">${renderRecipeRowActions(state, row)}</span>
                ${row.submission ? state.submissionActionErrors[row.submission.review_id] ? `
                  <span class="row-error">${e(state.submissionActionErrors[row.submission.review_id]!)}</span>
                ` : '' : ''}
              </div>
            `).join('')}
          </div>
          ${selected ? renderRecipeDrawer(state, selected) : ''}
        </div>
      ` : ''}
    </section>
  `;
};

// ────────────────────────────────────────────────────────────────
// Operation projection — shared publish-validation helpers that read an
// artifact's declared operations to summarise risk + approval intent.
// ────────────────────────────────────────────────────────────────

interface OperationProjection {
  id: string;
  riskTier: string;
  approvalIntent: string;
  groups: string[];
  requiredScopes: string[];
}

const normalizeRiskTier = (value: unknown): OperationRiskTier => {
  const risk = firstString(value)?.toLowerCase().replace(/[\s-]+/g, '_');
  if (risk === 'read' || risk === 'write' || risk === 'admin' || risk === 'destructive') return risk;
  return 'unknown';
};

const operationProjections = (payload: Record<string, unknown>): OperationProjection[] => {
  const ops = recordAt(payload.operations);
  return Object.entries(ops)
    .filter(([, value]) => isRecord(value))
    .map(([key, value]) => {
      const record = value as Record<string, unknown>;
      return {
        id: firstString(record.operation_id, key) ?? key,
        riskTier: normalizeRiskTier(record.risk_tier),
        approvalIntent: firstString(record.approval, record.approval_intent) ?? 'Default policy',
        groups: stringArray(record.groups),
        requiredScopes: stringArray(record.required_scopes),
      };
    })
    .sort((a, b) => a.id.localeCompare(b.id));
};

const uniqueSorted = (values: string[]): string[] =>
  [...new Set(values.filter((value) => value.trim().length > 0))].sort((a, b) => a.localeCompare(b));

// ────────────────────────────────────────────────────────────────
// Publish
// ────────────────────────────────────────────────────────────────

const publishArtifactPayload = (value: Record<string, unknown>): Record<string, unknown> =>
  recordAt(value.manifest ?? value.recipe ?? value);

const publishSlugFor = (payload: Record<string, unknown>): string | null =>
  firstString(payload.slug, payload.recipe_id, payload.pack_id, itemMeta(payload).slug);

const rewritePublishSlug = (publisherHandle: string | null, slug: string | null): string | null => {
  if (!slug) return null;
  const localless = slug.replace(/^local\//, '');
  if (localless.includes('/')) return localless;
  return publisherHandle ? `${publisherHandle}/${localless}` : localless;
};

const hasBundledIngredientManifest = (payload: Record<string, unknown>): boolean => {
  const bundled = payload.ingredients ?? payload.bundled_ingredients;
  return Array.isArray(bundled) && bundled.some((entry) => isRecord(entry));
};

/** R19 — the ingredient slugs a pack *provides*: the ingredients defined in
 *  its composition blocks (`contents[].composition.ingredients[].slug`). This
 *  is "provides" (defines), not "uses" (a recipe step that references one) —
 *  only a defining pack satisfies a recipe's dependency. Mirrors the
 *  composition walk in `scripts/pack-trust.mjs`. */
const packProvidedIngredientSlugs = (packs: PublishedItem[] | undefined): Set<string> => {
  const slugs = new Set<string>();
  for (const pack of packs ?? []) {
    const manifest = itemPayload(pack);
    const contents = Array.isArray(manifest.contents) ? manifest.contents : [];
    for (const entry of contents) {
      const record = recordAt(entry);
      const comp = isRecord(record.composition)
        ? record.composition
        : record.type === 'composition'
          ? record
          : null;
      if (!comp) continue;
      const ingredients = Array.isArray((comp as Record<string, unknown>).ingredients)
        ? (comp as Record<string, unknown>).ingredients as unknown[]
        : [];
      for (const ing of ingredients) {
        const slug = firstString(recordAt(ing).slug);
        if (slug) slugs.add(slug);
      }
    }
  }
  return slugs;
};

const contextItemSlugs = (
  publisherHandle: string | null,
  items: PublishedItem[] | undefined,
  kind: 'recipe' | 'pack',
): Array<{ slug: string; state: SlugReservationState }> =>
  (items ?? []).flatMap((item, index) => {
    const payload = itemPayload(item);
    const meta = itemMeta(payload);
    const slug = kind === 'recipe'
      ? firstString(item.slug, item.recipe_id, payload.recipe_id, meta.slug) ?? `recipe-${index + 1}`
      : firstString(item.slug, payload.slug, payload.pack_id, meta.slug) ?? `pack-${index + 1}`;
    return [
      { slug, state: itemSlugReservationState(item) },
      { slug: canonicalSlug(item.publisher_id || publisherHandle, slug), state: itemSlugReservationState(item) },
    ];
  });

const resolvePublishSlugReservation = (
  publishType: 'recipe' | 'pack',
  rewrittenSlug: string | null,
  publisherHandle: string | null,
  payload: Record<string, unknown>,
  context: PublishValidationContext,
): SlugReservationState | null => {
  if (!rewrittenSlug) return null;
  const now = context.now ?? Date.now();
  const explicit = normalizeSlugReservationState(payload.slug_reservation_state ?? payload.reservation_state);
  if (explicit) return explicit;

  const matchingSubmission = (context.submissions ?? [])
    .filter((submission) => submission.type === publishType)
    .find((submission) => slugMatches(publisherHandle, submission.slug, rewrittenSlug));
  if (matchingSubmission) return slugReservationStateForSubmission(matchingSubmission, now);

  const items = publishType === 'recipe' ? context.myRecipes : context.myPacks;
  const matchingItem = contextItemSlugs(publisherHandle, items, publishType)
    .find((item) => slugMatches(publisherHandle, item.slug, rewrittenSlug));
  return matchingItem?.state ?? 'available';
};

export const validatePublishDraft = (
  publishType: 'recipe' | 'pack',
  publishJson: string,
  publisherHandle: string | null,
  context: PublishValidationContext = {},
): PublishDraftValidation => {
  const issues: PublishValidationIssue[] = [];
  let parsed: Record<string, unknown> | null = null;
  let payload: Record<string, unknown> = {};

  if (!publisherHandle) {
    issues.push({
      severity: 'error',
      kind: 'slug-reservation',
      label: 'Publisher namespace required',
      detail: 'Set a publisher handle before publishing an artifact.',
      path: 'publisher',
      code: 'publisher_required',
    });
  }

  if (!publishJson.trim()) {
    issues.push({
      severity: 'pending',
      kind: 'source',
      label: 'Artifact source waiting',
      detail: 'Use a Kitchen handoff, upload an exported JSON file, or paste exported JSON as a recovery path.',
      path: '$',
      code: 'artifact_source_waiting',
    });
  } else {
    try {
      const decoded = JSON.parse(publishJson) as unknown;
      if (isRecord(decoded)) {
        payload = publishArtifactPayload(decoded);
        parsed = payload;
      } else {
        issues.push({
          severity: 'error',
          kind: 'schema',
          label: 'JSON object required',
          detail: 'Publishing accepts one exported artifact object.',
          path: '$',
          code: 'json_object_required',
        });
      }
    } catch (err) {
      issues.push({
        severity: 'error',
        kind: 'schema',
        label: 'JSON parse failed',
        detail: err instanceof Error ? err.message : 'The artifact JSON could not be parsed.',
        path: '$',
        code: 'json_parse_failed',
      });
    }
  }

  const meta = itemMeta(payload);
  const slug = publishSlugFor(payload);
  const rewrittenSlug = rewritePublishSlug(publisherHandle, slug);
  const name = firstString(payload.name, meta.name, slug);
  const description = firstString(payload.description, meta.description);
  const version = firstString(payload.version, meta.version) ?? '1';
  const tags = stringArray(payload.tags).length > 0 ? stringArray(payload.tags) : stringArray(meta.tags);
  const platforms = stringArray(payload.supported_platforms).length > 0
    ? stringArray(payload.supported_platforms)
    : stringArray(meta.supported_platforms);
  const dependencySlugs = collectDependencySlugs(payload);
  const operations = operationProjections(payload);
  const approvalSummary = uniqueSorted(operations.map((op) => op.approvalIntent)).join(', ') || 'Default policy';
  const riskSummary = dependencySlugs.length > 0
    ? `${dependencySlugs.length} dependencies`
    : 'Gates run at publish';
  const slugReservationState = resolvePublishSlugReservation(publishType, rewrittenSlug, publisherHandle, payload, context);
  const slugReservationDetailText = slugReservationDetail(slugReservationState, rewrittenSlug);

  if (parsed) {
    if (!slug) {
      issues.push({
        severity: 'error',
        kind: 'schema',
        label: 'Slug required',
        detail: 'The exported artifact must declare a slug or recipe_id.',
        path: 'slug',
        code: 'slug_required',
      });
    } else if (!/^[a-z0-9][a-z0-9._/-]*$/.test(slug)) {
      issues.push({
        severity: 'error',
        kind: 'schema',
        label: 'Slug format',
        detail: 'Use lowercase letters, numbers, dots, underscores, dashes, and slashes only.',
        path: 'slug',
        code: 'slug_format',
      });
    } else if (slug.startsWith('local/')) {
      issues.push({
        severity: 'warning',
        kind: 'slug-reservation',
        label: 'Slug rewrite preview',
        detail: `Publishing will reserve ${rewrittenSlug ?? slug} instead of local-only ${slug}.`,
        path: 'slug',
        code: 'slug_rewrite_preview',
      });
    }

    if (!name) {
      issues.push({
        severity: 'warning',
        kind: 'schema',
        label: 'Summary name missing',
        detail: 'The artifact still publishes, but the marketplace preview will fall back to the slug.',
        path: 'name',
        code: 'summary_name_missing',
      });
    }

    if (publishType === 'recipe' && hasBundledIngredientManifest(payload)) {
      issues.push({
        severity: 'error',
        kind: 'marketplace-gate',
        label: 'Bundled ingredient rejected',
        detail: 'A recipe cannot inline a custom ingredient manifest. Ship the ingredient inside a pack (recipe → pack → ingredient) and publish the pack.',
        path: 'ingredients',
        code: 'bundled_ingredient_rejected',
      });
    }

    // R19 — a standalone recipe's ingredient deps must resolve to already-
    // published packs (own OR others'). The dashboard can only see the
    // author's OWN packs, so this is a non-blocking heads-up; the authoritative
    // resolution against ALL published packs runs server-side at publish.
    if (publishType === 'recipe' && dependencySlugs.length > 0) {
      // Match leniently on both sides — a dep declared canonically
      // (publisher/x) must resolve to a pack that provides it bare (x), and
      // vice versa. Normalise the provided set to carry both forms.
      const provided = new Set<string>();
      for (const slug of packProvidedIngredientSlugs(context.myPacks)) {
        provided.add(slug);
        provided.add(canonicalSlug(publisherHandle, slug));
      }
      const unresolved = dependencySlugs.filter(
        (slug) => !provided.has(slug) && !provided.has(canonicalSlug(publisherHandle, slug)),
      );
      if (unresolved.length === 0) {
        issues.push({
          severity: 'ok',
          kind: 'dependency',
          label: `${dependencySlugs.length} ingredient ${dependencySlugs.length === 1 ? 'dependency resolves' : 'dependencies resolve'} to your published packs`,
          detail: 'Every ingredient dependency is provided by a pack you have published.',
          path: 'steps',
          code: 'dependencies_resolved_own_packs',
        });
      } else {
        issues.push({
          severity: 'warning',
          kind: 'dependency',
          label: `${unresolved.length} ingredient ${unresolved.length === 1 ? 'dependency' : 'dependencies'} not in your published packs`,
          detail: `${unresolved.join(', ')} ${unresolved.length === 1 ? 'is' : 'are'} not provided by a pack you have published. Publishing resolves dependencies against all published packs (yours or others'); a custom or unpublished ingredient → publish a pack carrying it (recipe → pack → ingredient).`,
          path: 'steps',
          code: 'dependency_unresolved_own_packs',
        });
      }
    }

    if (slugReservationState) {
      const severity: PublishValidationSeverity = slugReservationState === 'reserved-pending' || slugReservationState === 'unavailable'
        ? 'error'
        : slugReservationState === 'available'
          ? 'ok'
          : 'warning';
      issues.push({
        severity,
        kind: 'slug-reservation',
        label: `Slug reservation: ${slugReservationLabel(slugReservationState)}`,
        detail: slugReservationDetailText,
        path: 'slug',
        code: `slug_${slugReservationState.replace(/-/g, '_')}`,
      });
    }
  }

  issues.push({
    severity: 'pending',
    kind: 'marketplace-gate',
    label: 'Cloud validation endpoint pending',
    detail: 'The dashboard runs local structure checks here; final schema, policy, dependency, and slug-reservation gates run on publish.',
    path: '$',
    code: 'cloud_validation_pending',
  });

  return {
    parsed,
    issues,
    slug,
    rewrittenSlug,
    name,
    description,
    version,
    tags,
    platforms,
    executionScope: executionScopeLabel(payload, meta),
    riskSummary,
    approvalSummary,
    provenanceLabel: provenanceLabel(payload, meta),
    forkLineage: forkLineageLabel(payload.fork_of, meta.fork_of),
    dependencySlugs,
    slugReservationState,
    slugReservationDetail: slugReservationDetailText,
    canSubmit: Boolean(parsed && publishJson.trim()) && !issues.some((issue) => issue.severity === 'error'),
  };
};

const validationChipClass = (severity: PublishValidationSeverity): string =>
  severity === 'ok' ? 'published' : severity;

const validationSeverityLabel = (severity: PublishValidationSeverity): string =>
  severity === 'ok' ? 'OK' : severity.charAt(0).toUpperCase() + severity.slice(1);

/** The human-readable check list. The machine kind/code identifiers stay on
 *  the row's title attribute for support triage — they are not user copy. */
const renderValidationIssues = (issues: PublishValidationIssue[]): string => `
  <div class="version-history">
    <h4>Structured validation results</h4>
    <ol>
      ${issues.map((issue) => `
        <li title="${e(issue.kind)}${issue.code ? ` / ${e(issue.code)}` : ''}">
          <span>
            <span class="chip chip-${chipClass(issue.severity)}">${e(validationSeverityLabel(issue.severity))}</span>
            ${e(issue.label)}${issue.path && issue.path !== '$' ? ` (${e(issue.path)})` : ''}
          </span>
          <time>${e(issue.detail)}</time>
        </li>
      `).join('')}
    </ol>
  </div>
`;

/** DB-6 — the publish draft at-a-glance summary. Formerly a right-side
 *  "validation rail"; the page is now single-column, so this renders inline as
 *  a full-width card just before Submit. Same trust-preview fields, no info
 *  loss — only the layout moved. */
const renderPublishDraftSummary = (state: DashboardState, validation: PublishDraftValidation): string => `
  <section class="account-card account-section publish-draft-summary" data-publish-summary>
    <div class="account-section-head">
      <h3>Draft summary${validation.name ? ` — ${e(validation.name)}` : ''}</h3>
      <span class="chip chip-${validation.canSubmit ? 'published' : 'pending'}">${validation.canSubmit ? 'Ready to publish' : 'Not ready'}</span>
    </div>
    <div class="summary-rows">
      <div class="drawer-row"><span>Artifact</span><strong>${state.publishType === 'recipe' ? 'Recipe' : 'Pack'}</strong></div>
      <div class="drawer-row"><span>Source</span><strong>${e(state.publishSource)}</strong></div>
      <div class="drawer-row"><span>Slug preview</span><code>${e(validation.rewrittenSlug ?? validation.slug ?? 'Waiting for slug')}</code></div>
      <div class="drawer-row"><span>Reservation</span><strong>${e(slugReservationLabel(validation.slugReservationState))}</strong></div>
      <div class="drawer-row"><span>Version</span><strong>v${e(validation.version)}</strong></div>
      <div class="drawer-row"><span>Platforms/kind</span><strong>${validation.platforms.length ? e(validation.platforms.join(', ')) : e(firstString(validation.parsed?.kind) ?? 'Not declared')}</strong></div>
      <div class="drawer-row"><span>Execution scope</span><strong>${e(validation.executionScope)}</strong></div>
      <div class="drawer-row"><span>Risk / approval</span><strong>${e(validation.riskSummary)} / ${e(validation.approvalSummary)}</strong></div>
      <div class="drawer-row"><span>Provenance</span><strong>${e(validation.provenanceLabel)}</strong></div>
      <div class="drawer-row"><span>Fork lineage</span><strong>${validation.forkLineage ? e(validation.forkLineage) : 'None declared'}</strong></div>
    </div>
    ${validation.description ? `<p class="drawer-desc">${e(validation.description)}</p>` : '<p class="drawer-desc">No summary description is available in the artifact.</p>'}
    <p class="drawer-desc">${e(validation.slugReservationDetail ?? 'Slug reservation pending.')}</p>
    ${validation.tags.length > 0 ? `<div class="tag-row">${validation.tags.map((tag) => `<span class="tag-pill">${e(tag)}</span>`).join('')}</div>` : ''}
    ${validation.dependencySlugs.length > 0 ? `
      <div class="version-history">
        <h4>Dependencies</h4>
        <ol>${validation.dependencySlugs.map((slug) => `<li><span>${e(slug)}</span></li>`).join('')}</ol>
      </div>
    ` : ''}
    <div class="version-history">
      <h4>After you publish</h4>
      <ol>
        <li><span>Slug reservation</span><time>Publishing reserves your slug in the publisher namespace.</time></li>
        <li><span>Cancel window</span><time>A pending record can be cancelled from its row in the Recipes console.</time></li>
        <li><span>Review status</span><time>Track the record in the In-review tab; no reviewer-private notes are shown.</time></li>
      </ol>
    </div>
  </section>
`;

// ────────────────────────────────────────────────────────────────
// Packs console — published-pack management. Packs publish through
// the marketplace worker directly (no review pipeline / submissions
// merge), so the console is a flat table over `state.myPacks` with
// the same unpublish/republish control as recipes.
// ────────────────────────────────────────────────────────────────

const packRecipeCount = (item: PublishedItem): number => {
  const manifest = item.manifest;
  if (!manifest || typeof manifest !== 'object') return 0;
  const recipes = (manifest as { recipes?: unknown }).recipes;
  return Array.isArray(recipes) ? recipes.length : 0;
};

const renderMyPacks = (state: DashboardState): string => {
  const packs = state.myPacks;
  return `
    <section class="dash-page console-page">
      <div class="page-header">
        <div>
          <h2>Packs</h2>
          <p class="page-subtitle">Manage published packs. Authoring and new versions happen in Kitchen; unpublish removes a pack from the marketplace listing without touching existing installs.</p>
        </div>
        <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" data-action="open-kitchen">Open Kitchen</a>
      </div>

      ${state.loading ? renderConsoleSkeleton('Loading packs') : ''}
      ${packs.length === 0 && !state.loading ? renderEmptyState(
        EMPTY_STATE_ICONS.packs,
        'No packs published yet',
        'Bundle recipes and ingredients into a pack in Kitchen, then publish it from the Publish page.',
        `<a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-primary btn-sm" data-action="open-kitchen">Open Kitchen</a><a href="#/publish" class="btn btn-secondary btn-sm">Go to Publish</a>`,
      ) : ''}
      ${packs.length > 0 ? `
        <div class="console-table pack-console-table" role="table" aria-label="Publisher packs">
          <div class="console-row console-head pack-console-row" role="row">
            <span role="columnheader">Name</span>
            <span role="columnheader">Slug</span>
            <span role="columnheader">Version</span>
            <span role="columnheader">Recipes</span>
            <span role="columnheader">Status</span>
            <span role="columnheader">Installs</span>
            <span role="columnheader">Updated</span>
            <span role="columnheader">Actions</span>
          </div>
          ${packs.map((pack) => {
            const slug = pack.slug ?? '';
            const status = pack.status ?? 'published';
            const manifest = (pack.manifest && typeof pack.manifest === 'object') ? pack.manifest as Record<string, unknown> : {};
            const repo = typeof manifest.repo === 'string' ? manifest.repo : '';
            const description = pack.description ?? (typeof manifest.description === 'string' ? manifest.description : '');
            return `
              <div class="console-row pack-console-row" role="row">
                <span class="row-title" role="cell">${e(pack.name ?? slug)}</span>
                <code role="cell" data-label="Slug">${e(slug)}</code>
                <span role="cell" data-label="Version">v${pack.version ?? 1}</span>
                <span role="cell" data-label="Recipes">${packRecipeCount(pack)}</span>
                <span role="cell" data-label="Status"><span class="chip chip-${chipClass(status)}">${e(statusLabel(status === 'unpublished' ? 'unpublished' : 'published'))}</span></span>
                <span role="cell" data-label="Installs">${pack.download_count ?? pack.downloads ?? 0}</span>
                <span role="cell" data-label="Updated" title="${e(formatDateLabel(pack.updated_at ?? pack.created_at ?? null))}">${e(formatShortDate(pack.updated_at ?? pack.created_at ?? null))}</span>
                <span role="cell" data-label="Actions">
                  <div class="row-actions">
                    <button type="button" class="btn btn-secondary btn-sm" data-action="copy-text"
                      data-copy-value="${e(slug)}" data-copy-label="Pack slug">Copy slug</button>
                    ${renderMetaEditButton('pack', slug, repo, description)}
                    ${renderStatusToggle(state, 'pack', slug, status)}
                  </div>
                </span>
                ${renderMetaEditor(state, 'pack', slug)}
              </div>
            `;
          }).join('')}
        </div>
      ` : ''}
    </section>
  `;
};

const PUBLISH_SUCCESS_ICON =
  '<svg viewBox="0 0 24 24" width="22" height="22" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round"><path d="M20 6L9 17l-5-5"/></svg>';

/** Section switch on #/publish — the publish form vs the in-review list. */
const renderPublishSectionTabs = (state: DashboardState): string => {
  const pending = state.submissions.filter((sub) => sub.status === 'pending').length;
  return `
      <div class="publish-toggle publish-section-toggle" role="tablist" aria-label="Publish sections">
        <button type="button" class="auth-tab ${state.publishTab === 'publish' ? 'active' : ''}"
          data-action="publish-tab" data-value="publish">Publish</button>
        <button type="button" class="auth-tab ${state.publishTab === 'review' ? 'active' : ''}"
          data-action="publish-tab" data-value="review">In review${pending > 0 ? ` (${pending})` : ''}</button>
      </div>`;
};

/** The in-review records list. No paging: the worker returns the latest 100
 *  (submitted_at desc) and a publisher's own records number in the tens — a
 *  cap note renders only if the limit is actually hit. Rows are read-only;
 *  the only extra affordance is the reviewer's reason on a rejection. */
const renderPublishReviewList = (state: DashboardState): string => {
  const subs = state.submissions;
  if (subs.length === 0) {
    return renderEmptyState(
      EMPTY_STATE_ICONS.submissions,
      'Nothing in review',
      'Recued review records for your published items appear here — pending, approved, or rejected with the reviewer’s reason.',
      '',
    );
  }
  return `
      <div class="review-list">
        ${subs.map((sub) => `
          <div class="review-row">
            <div class="review-row-head">
              <span class="chip chip-${chipClass(sub.status)}">${e(statusLabel(sub.status))}</span>
              <strong>${e(sub.name || sub.slug)}</strong>
              <code>${e(sub.slug)}</code>
              <span class="review-row-meta" title="${e(formatDateLabel(sub.submitted_at))}">${e(sub.type)} · v${sub.version} · ${e(formatShortDate(sub.submitted_at))}</span>
            </div>
            ${sub.status === 'rejected' ? `<p class="review-reject-reason">${e(sub.rejection_reason ?? 'No reason recorded.')}</p>` : ''}
          </div>
        `).join('')}
        ${subs.length >= 100 ? '<p class="account-hint">Showing the latest 100 review records.</p>' : ''}
      </div>`;
};

const renderPublish = (state: DashboardState): string => {
  const header = `
      <div class="page-header">
        <div>
          <h2>Publish to marketplace</h2>
          <p class="page-subtitle">Validate a finished Kitchen artifact and publish it to the marketplace. Artifact edits stay in Kitchen.</p>
        </div>
        <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm" data-action="open-kitchen">Return to Kitchen</a>
      </div>`;

  // No handle yet — the form is dead until the namespace exists, so claim it
  // here instead of rendering a wall of waiting placeholders.
  if (!state.publisherHandle) {
    return `
    <section class="dash-page console-page">
      ${header}
      <div class="flash flash-info">Set your publisher handle first — everything you publish lives under it.</div>
      ${renderPublisherSetup(state)}
    </section>
  `;
  }

  // In-review section — the records list replaces the form wholesale.
  if (state.publishTab === 'review') {
    return `
    <section class="dash-page console-page">
      ${header}
      ${renderPublishSectionTabs(state)}
      ${renderPublishReviewList(state)}
    </section>
  `;
  }

  // Published — a focused completion panel; the form (already cleared) would
  // only re-render as empty placeholders under the flash.
  if (state.publishStatus === 'success') {
    const consoleHref = state.publishType === 'pack' ? '#/packs' : '#/recipes';
    const consoleLabel = state.publishType === 'pack' ? 'View packs' : 'View recipes';
    return `
    <section class="dash-page console-page">
      ${header}
      ${renderPublishSectionTabs(state)}
      <div class="flash flash-ok">${e(state.publishMessage ?? 'Published to the marketplace.')}</div>
      ${renderEmptyState(
        PUBLISH_SUCCESS_ICON,
        'Published to the marketplace',
        'It is live under your handle. Manage it from the console — self-published items show as unverified until Recued review computes their trust summary.',
        `<a href="${consoleHref}" class="btn btn-primary btn-sm">${consoleLabel}</a>
         <button type="button" class="btn btn-secondary btn-sm" data-action="publish-reset">Publish another</button>
         <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm" data-action="open-kitchen">Return to Kitchen</a>`,
      )}
    </section>
  `;
  }

  const validation = validatePublishDraft(state.publishType, state.publishJson, state.publisherHandle, {
    submissions: state.submissions,
    myRecipes: state.myRecipes,
    myPacks: state.myPacks,
  });
  const parsed = Boolean(validation.parsed);
  const firstRun = !state.publishJson.trim();
  const parseIssue = !parsed && !firstRun
    ? validation.issues.find((issue) => issue.kind === 'schema')
    : undefined;
  const slugOk = parsed && Boolean(validation.slug)
    && !validation.issues.some((issue) => issue.severity === 'error' && issue.path === 'slug');
  const steps: Array<{ label: string; done: boolean; active: boolean }> = [
    { label: 'Artifact source', done: parsed, active: !parsed },
    { label: 'Namespace/slug', done: slugOk, active: parsed && !slugOk },
    { label: 'Validation/policy', done: validation.canSubmit, active: slugOk && !validation.canSubmit },
    { label: 'Publish', done: false, active: validation.canSubmit },
  ];
  return `
    <section class="dash-page console-page">
      ${header}
      ${renderPublishSectionTabs(state)}
      ${state.publishStatus === 'error' ? `
        <div class="flash flash-error">${e(state.publishMessage ?? 'Publish failed.')}</div>
      ` : ''}
      <div class="status-tabs" role="tablist" aria-label="Publish steps">
            ${steps.map((step) => `
              <span class="status-tab ${step.active ? 'active' : ''}${step.done ? ' done' : ''}">${step.done ? '✓ ' : ''}${e(step.label)}</span>
            `).join('')}
          </div>

          <section class="account-card account-section">
            <div class="account-section-head">
              <h3>Artifact source</h3>
              <span class="chip chip-${validationChipClass(parsed ? 'ok' : parseIssue ? 'error' : 'pending')}">${parsed ? 'Loaded' : parseIssue ? 'Invalid' : 'Waiting'}</span>
            </div>
            <div class="publish-toggle">
              <button type="button" class="auth-tab ${state.publishType === 'recipe' ? 'active' : ''}"
                data-action="publish-type" data-value="recipe">Recipe</button>
              <button type="button" class="auth-tab ${state.publishType === 'pack' ? 'active' : ''}"
                data-action="publish-type" data-value="pack">Pack</button>
            </div>
            <p class="account-hint">A pack is the standard bundle unit — publish a pack to ship its recipes and any custom ingredients together (recipe → pack → ingredient). A standalone recipe is fine when its dependencies resolve to already-published packs.</p>
            <div class="row-actions">
              <button type="button" class="btn btn-secondary btn-sm ${state.publishSource === 'kitchen' ? 'chip-bound' : ''}"
                data-action="publish-source" data-value="kitchen">Kitchen handoff</button>
              <button type="button" class="btn btn-secondary btn-sm ${state.publishSource === 'upload' ? 'chip-bound' : ''}"
                data-action="publish-source" data-value="upload">Uploaded JSON</button>
              <button type="button" class="btn btn-secondary btn-sm ${state.publishSource === 'paste' ? 'chip-bound' : ''}"
                data-action="publish-source" data-value="paste">Pasted JSON</button>
              <input type="file" data-file-field="publish-json-file" accept="application/json,.json"
                class="publish-file-input${state.publishSource === 'upload' ? '' : ' file-input-hidden'}" />
            </div>
            <label class="form-label">Exported ${state.publishType} JSON</label>
            <textarea data-field="publish-json" class="${parseIssue ? 'textarea-error' : ''}" placeholder='{ "slug": "local/my-artifact", "name": "..." }' spellcheck="false">${e(state.publishJson)}</textarea>
            ${parseIssue ? `<p class="field-error">${e(parseIssue.label)} — ${e(parseIssue.detail)}</p>` : ''}
            ${firstRun ? `
              <ol class="publish-guide">
                <li><strong>Export in Kitchen.</strong> Finish the ${state.publishType} there and export its JSON.</li>
                <li><strong>Load it here.</strong> A Kitchen handoff fills this form for you; uploading or pasting the exported file works the same.</li>
                <li><strong>Review and publish.</strong> Local checks run as soon as the artifact loads; publishing reserves your marketplace slug.</li>
              </ol>
            ` : ''}
          </section>

          ${parsed ? `
          <section class="account-card account-section">
            <div class="account-section-head">
              <h3>Namespace and slug confirmation</h3>
              <span class="chip chip-${chipClass(validation.slugReservationState ?? 'pending')}">${e(slugReservationLabel(validation.slugReservationState))}</span>
            </div>
            <div class="drawer-row"><span>Local slug</span><code>${e(validation.slug ?? 'Not set')}</code></div>
            <div class="drawer-row"><span>Marketplace slug</span><code>${e(validation.rewrittenSlug ?? 'Not set')}</code></div>
            <p class="account-hint">${e(validation.slugReservationDetail ?? 'Local-only slugs are rewritten into the publisher namespace before they are reserved on publish.')}</p>
          </section>

          <section class="account-card account-section">
            <div class="account-section-head">
              <h3>Validation and policy checks</h3>
              <span class="chip chip-${validation.canSubmit ? 'published' : 'pending'}">${validation.canSubmit ? 'No blocking local errors' : 'Fix errors first'}</span>
            </div>
            ${renderValidationIssues(validation.issues)}
          </section>

          ${renderPublishDraftSummary(state, validation)}

          <section class="account-card account-section">
            <div class="account-section-head">
              <h3>Publish</h3>
              <span class="chip chip-pending">Live on publish</span>
            </div>
            <p class="account-hint">Publishing pushes the artifact to the marketplace under your handle immediately. Self-published items show as unverified until Recued review computes their trust summary.</p>
            <div class="publish-actions">
              <button type="button" class="btn btn-primary" data-action="publish"
                ${state.publishStatus === 'publishing' || !validation.canSubmit ? 'disabled' : ''}>
                ${state.publishStatus === 'publishing' ? 'Publishing…' : 'Publish to marketplace'}
              </button>
              <a href="${KITCHEN_URL}" target="_blank" rel="noopener" class="btn btn-secondary" data-action="open-kitchen">Return to Kitchen</a>
              <button type="button" class="btn btn-secondary" data-action="publish-tab" data-value="review">View review status</button>
            </div>
          </section>
          ` : ''}
    </section>
  `;
};

// ────────────────────────────────────────────────────────────────
// Account
// ────────────────────────────────────────────────────────────────

const publisherPageUrl = (handle: string): string =>
  `${MARKETPLACE_URL}/publishers/${encodeURIComponent(handle)}`;

const formatTimestamp = (value: number | undefined): string => {
  if (!value || value <= 0) return 'Unknown';
  return `${new Date(value).toISOString().slice(0, 16).replace('T', ' ')} UTC`;
};

const convenienceStatusLabel = (status: ProConvenienceStatus): string => {
  switch (status) {
    case 'active': return 'Active';
    case 'awaiting-server': return 'Awaiting server';
    case 'error': return 'Error';
    case 'not-configured':
    default: return 'Not configured';
  }
};

const renderProConvenienceStrip = (state: DashboardState): string => `
  <section class="account-card account-section">
    <div class="account-section-head">
      <h3>Pro convenience status</h3>
      <span class="chip chip-plan chip-${chipClass(state.plan)}">${e(state.plan)}</span>
    </div>
    <div class="status-strip">
      ${state.proConveniences.map((item) => {
        // Skip a detail line that only repeats the status label ("Not
        // configured." under "Not configured") — it reads as a stutter.
        const detailRepeatsStatus = item.detail.replace(/\.\s*$/, '').trim().toLowerCase()
          === convenienceStatusLabel(item.status).toLowerCase();
        return `
        <div class="status-item">
          <span class="status-label">${e(item.label)}</span>
          <strong class="status-value status-${chipClass(item.status)}">${e(convenienceStatusLabel(item.status))}</strong>
          ${item.detail && !detailRepeatsStatus ? `<span class="status-detail">${e(item.detail)}</span>` : ''}
        </div>
      `; }).join('')}
    </div>
  </section>
`;

const renderServerBindings = (state: DashboardState): string => {
  const read = state.accountBindingRead;
  if (read.status === 'pending-worker-endpoint') {
    return `
      <section class="account-card account-section" data-account-section="server-bindings">
        <div class="account-section-head">
          <h3>Server bindings - pending</h3>
          <span class="chip chip-binding chip-no-server">Pending endpoint</span>
        </div>
        <p class="account-hint">The Worker bound-server read endpoint is not available yet. Binding and unbinding stay in the webclient Settings - Account flow.</p>
        <div class="account-empty">No dashboard binding rows are fabricated before the Worker endpoint lands.</div>
      </section>
    `;
  }
  if (read.status === 'error') {
    return `
      <section class="account-card account-section" data-account-section="server-bindings">
        <div class="account-section-head">
          <h3>Server bindings</h3>
          <span class="chip chip-conflict">Unavailable</span>
        </div>
        <div class="flash flash-error">${e(read.error ?? 'Could not load server bindings.')}</div>
      </section>
    `;
  }
  return `
    <section class="account-card account-section" data-account-section="server-bindings">
      <div class="account-section-head">
        <h3>Server bindings</h3>
        <span class="chip chip-binding chip-${chipClass(state.bindingStatus)}">${e(state.bindingStatus)}</span>
      </div>
      ${state.bindingStatus === 'Conflict' ? `
        <div class="flash flash-warn" data-account-binding-conflict>
          <strong>Binding conflict.</strong> A recent attempt to bind a server to this account
          hit an ownership conflict — another account already owns that server and the takeover
          was not confirmed. Binding runs from the server's webclient, so confirm or cancel the
          rebind there (Settings &rarr; Account). If that was not you, no action is needed — the
          other account keeps the server. The server involved is listed in the Binding audit below;
          to release a server you own, use <strong>Unbind</strong>.
        </div>
      ` : ''}
      ${read.bindings.length === 0 ? `
        <div class="account-empty">No server is bound to this account yet. Start binding from the webclient Settings - Account page.</div>
      ` : `
        <div class="account-table" role="table" aria-label="Bound servers">
          <div class="account-table-row account-table-head" role="row">
            <span role="columnheader">Server</span>
            <span role="columnheader">Account</span>
            <span role="columnheader">First bound</span>
            <span role="columnheader">Last rebind</span>
            <span role="columnheader">Actions</span>
          </div>
          ${read.bindings.map((binding) => {
            const actionMessage = state.bindingActionMessages[binding.server_fingerprint];
            const unbinding = state.bindingUnbindingId === binding.server_fingerprint;
            const rotating = state.bindingRotatingId === binding.server_fingerprint;
            return `
            <div class="account-table-row" role="row">
              <code role="cell" data-label="Server">${e(binding.server_fingerprint)}</code>
              <span role="cell" data-label="Account">${e(binding.account_id)}${binding.publisher_handle ? ` (@${e(binding.publisher_handle)})` : ''}</span>
              <span role="cell" data-label="First bound">${e(formatTimestamp(binding.bound_at))}</span>
              <span role="cell" data-label="Last rebind">${e(formatTimestamp(binding.rebound_at))}</span>
              <span role="cell" data-label="Actions">
                <button type="button" class="btn btn-secondary btn-sm"
                  data-action="rotate-server-credential" data-server-fingerprint="${e(binding.server_fingerprint)}"
                  ${rotating || unbinding ? 'disabled aria-busy="true"' : ''}>${rotating ? 'Rotating' : 'Rotate credential'}</button>
                <button type="button" class="btn btn-secondary btn-sm danger-action"
                  data-action="unbind-server" data-server-fingerprint="${e(binding.server_fingerprint)}"
                  ${unbinding || rotating ? 'disabled aria-busy="true"' : ''}>${unbinding ? 'Unbinding' : 'Unbind server'}</button>
              </span>
              ${actionMessage ? `
                <span class="row-error ${actionMessage.kind === 'success' ? 'flash-ok' : ''}">${e(actionMessage.message)}</span>
              ` : ''}
            </div>
          `; }).join('')}
        </div>
      `}
    </section>
  `;
};

const renderBindingAudit = (state: DashboardState): string => {
  const read = state.accountBindingRead;
  if (read.status === 'pending-worker-endpoint') {
    return `
      <section class="account-card account-section" data-account-section="binding-audit">
        <div class="account-section-head">
          <h3>Binding audit - pending</h3>
          <span class="chip chip-binding chip-no-server">Pending endpoint</span>
        </div>
        <p class="account-hint">Recent bind, rebind, unbind, conflict, and exchange-failure events will appear here once the Worker read endpoint lands.</p>
        <div class="account-empty">No dashboard audit rows are fabricated before the Worker endpoint lands.</div>
      </section>
    `;
  }
  return `
    <section class="account-card account-section" data-account-section="binding-audit">
      <h3>Binding audit</h3>
      ${read.auditEvents.length === 0 ? `
        <div class="account-empty">No binding audit events are available.</div>
      ` : `
        <div class="account-table" role="table" aria-label="Binding audit">
          <div class="account-table-row account-table-head" role="row">
            <span role="columnheader">Action</span>
            <span role="columnheader">Server</span>
            <span role="columnheader">Actor</span>
            <span role="columnheader">Time</span>
          </div>
          ${read.auditEvents.map((event) => `
            <div class="account-table-row" role="row">
              <span role="cell" data-label="Action" title="${e(event.action)}">${e(bindingActivityLabel(event.action).title)}</span>
              <code role="cell" data-label="Server">${e(event.server_fingerprint)}</code>
              <span role="cell" data-label="Actor">${e(event.actor_surface)}${event.confirmed ? ' confirmed' : ''}</span>
              <span role="cell" data-label="Time">${e(formatTimestamp(event.timestamp))}</span>
            </div>
          `).join('')}
        </div>
      `}
    </section>
  `;
};

/** Canonical Pro list price (one source of truth for the dashboard). Paddle is
 *  the merchant of record, so the exact charged total — currency + local tax —
 *  is shown at checkout / on the receipt. */
export const PRO_PRICE_LABEL = '$5.99/mo';

/** What Pro provisions. Pro is a friction-reduction bundle (D-148), NOT a
 *  capability gate: a self-hoster gets the same reachability + certs + handle
 *  for free by bringing their own DDNS / ACME / domain, so the copy frames
 *  setup convenience. Labels track `defaultProConveniences`. */
const PRO_FEATURES: ReadonlyArray<string> = [
  '<handle>.recued.net subdomain — auto-updated DNS so your server stays reachable',
  'Automatic HTTPS certificate (ACME DNS-01), issued and renewed for you',
  'A reserved marketplace publisher handle',
];

/** Plan + price panel: an upgrade pitch on Free, a value confirmation on Pro. */
const renderPlanDetails = (state: DashboardState): string => {
  const isPro = state.plan === 'Pro';
  const intro = isPro ? 'Your subscription provisions:' : 'Skip the cloud setup — Pro provisions:';
  const footnote = isPro
    ? 'Billed by Paddle; currency and local taxes appear on your receipt.'
    : 'Prefer to self-host? Bring your own DDNS, certificate and domain — the server is free and AGPL. Billed by Paddle; taxes shown at checkout.';
  return `
          <div class="plan-details">
            <div class="plan-details-head">
              <strong>${isPro ? 'Your Recued Pro plan' : 'Recued Pro'}</strong>
              <span class="plan-price">${e(PRO_PRICE_LABEL)}</span>
            </div>
            <p class="account-hint">${e(intro)}</p>
            <ul class="plan-feature-list">
              ${PRO_FEATURES.map((f) => `<li>${e(f)}</li>`).join('')}
            </ul>
            <p class="account-hint">${e(footnote)}</p>
          </div>`;
};

/** Format a transaction's ISO datetime as a short calendar date; falls back to
 *  the raw string if it doesn't parse. */
const formatTransactionDate = (iso: string): string => {
  const ms = Date.parse(iso);
  if (Number.isNaN(ms)) return iso;
  return new Date(ms).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
};

/** Format a grand-total in the currency's lowest denomination (e.g. cents for
 *  USD) as a localized currency string. The minor-unit exponent is read from
 *  the currency's own locale data (`Intl` — 2 for USD/EUR, 0 for JPY/KRW, 3 for
 *  KWD), so zero-decimal currencies aren't rendered 100x too small. A
 *  non-numeric amount or unknown currency degrades to a plain rendering rather
 *  than throwing. */
const formatTransactionAmount = (amountMinor: string | null, currencyCode: string | null): string => {
  if (!amountMinor || !/^-?\d+$/.test(amountMinor)) return '—';
  const minor = Number(amountMinor);
  if (Number.isNaN(minor)) return '—';
  if (currencyCode && /^[A-Za-z]{3}$/.test(currencyCode)) {
    try {
      const fmt = new Intl.NumberFormat(undefined, { style: 'currency', currency: currencyCode.toUpperCase() });
      const exponent = fmt.resolvedOptions().maximumFractionDigits ?? 2;
      return fmt.format(minor / 10 ** exponent);
    } catch {
      /* unknown currency code — fall through to a plain number */
    }
  }
  return (minor / 100).toFixed(2);
};

const STATUS_LABELS: Record<string, string> = {
  completed: 'Paid',
  paid: 'Paid',
  billed: 'Billed',
  past_due: 'Past due',
  canceled: 'Canceled',
  cancelled: 'Canceled',
};

const transactionStatusLabel = (status: string): string =>
  STATUS_LABELS[status.toLowerCase()] ?? status;

/** Billing history: lazily loaded past Pro charges. Only offered for a
 *  manageable Pro subscription (the cloud holds a Paddle subscription id to
 *  query). Idle → a "View billing history" button; loaded → a compact list. */
const renderBillingHistory = (state: DashboardState): string => {
  if (!(state.plan === 'Pro' && state.subscriptionManageable)) return '';
  const status = state.billingHistoryStatus;
  if (status === 'idle') {
    return `
          <div class="billing-history">
            <button type="button" class="btn btn-secondary btn-sm" data-action="load-billing-history">View billing history</button>
          </div>`;
  }
  // First load (no page yet): a full-card loading / error view. Once a page has
  // been shown, a navigation loading/error keeps the page in place (below) so
  // the user never loses their spot.
  if (state.billingHistory === null) {
    if (status === 'error') {
      return `
          <div class="billing-history">
            <div class="flash flash-error">${e(state.billingHistoryMessage ?? 'Could not load billing history.')}</div>
            <button type="button" class="btn btn-secondary btn-sm" data-action="load-billing-history">Retry</button>
          </div>`;
    }
    return `
          <div class="billing-history">
            <p class="account-hint" aria-busy="true">Loading billing history…</p>
          </div>`;
  }
  const rows = state.billingHistory;
  const busy = status === 'loading';
  const hasPrev = state.billingHistoryPrevCursors.length > 0;
  const hasNext = state.billingHistoryNextCursor !== null;
  // A pager only when there's somewhere to go (more than one page total).
  // Buttons re-issue the page on click, so they double as the retry path for a
  // failed navigation (the displayed page stays on the last good one).
  const pager = hasPrev || hasNext
    ? `
            <div class="billing-history-pager">
              <button type="button" class="btn btn-secondary btn-xs" data-action="billing-history-prev" ${hasPrev && !busy ? '' : 'disabled'}>Previous</button>
              <button type="button" class="btn btn-secondary btn-xs" data-action="billing-history-next" ${hasNext && !busy ? '' : 'disabled'}>Next</button>
            </div>`
    : '';
  // Inline notice overlaid on the kept page during a navigation loading/error.
  const notice = busy
    ? '<p class="account-hint" aria-busy="true">Loading…</p>'
    : status === 'error'
      ? `<div class="flash flash-error">${e(state.billingHistoryMessage ?? 'Could not load billing history.')}</div>`
      : '';
  if (rows.length === 0) {
    return `
          <div class="billing-history">
            <p class="account-hint">${hasPrev ? 'No more charges on this page.' : 'No charges yet.'}</p>
            ${notice}
            ${pager}
          </div>`;
  }
  return `
          <div class="billing-history">
            <h4 class="billing-history-head">Billing history</h4>
            <ul class="billing-history-list">
              ${rows.map((t, i) => {
                const downloading = state.billingInvoiceIndex === i;
                return `
              <li class="billing-history-row">
                <span class="billing-history-date">${e(formatTransactionDate(t.occurredAt))}</span>
                <span class="billing-history-amount">${e(formatTransactionAmount(t.amountMinor, t.currencyCode))}</span>
                <span class="billing-history-status">${e(transactionStatusLabel(t.status))}</span>
                ${t.invoiceNumber ? `<span class="billing-history-invoice">${e(t.invoiceNumber)}</span>` : ''}
                <button type="button" class="btn btn-link btn-xs billing-history-download" data-action="download-invoice" data-index="${i}" ${state.billingInvoiceIndex !== null || busy ? 'disabled' : ''} ${downloading ? 'aria-busy="true"' : ''}>${downloading ? 'Preparing…' : 'Invoice'}</button>
              </li>`;
              }).join('')}
            </ul>
            ${pager}
            ${notice}
            ${state.billingInvoiceMessage ? `<div class="flash flash-error">${e(state.billingInvoiceMessage)}</div>` : ''}
          </div>`;
};

/** The Subscription and billing card. A Pro account with a cloud-held Paddle
 *  subscription id (`subscriptionManageable`) self-serves cancel + resume here;
 *  everything else routes to the hosted portal. The cancel is a period-end
 *  schedule (reversible via resume), gated behind an inline confirm. */
const renderSubscriptionCard = (state: DashboardState): string => {
  const isPro = state.plan === 'Pro';
  const dateLabel = state.billingRenewalLabel ?? null;
  const busy = state.billingActionStatus !== 'idle';
  const cancelling = state.billingActionStatus === 'cancelling';
  const resuming = state.billingActionStatus === 'resuming';
  const opening = state.billingActionStatus === 'opening-portal';
  // Dunning: Paddle keeps Pro on while it retries a failed charge, so the user
  // still has Pro but must update payment before the retries run out.
  const isPastDue = state.billingStatus === 'Past due';

  const statusText = state.cancelScheduled ? `${state.billingStatus} — ending` : state.billingStatus;
  const dateRowLabel = state.cancelScheduled ? 'Cancels on' : isPro ? 'Renews on' : 'Renewal or cancel date';

  // Self-serve action: resume when a cancel is scheduled, cancel otherwise.
  // Only offered when the cloud can drive Paddle for this account.
  const selfServe = isPro && state.subscriptionManageable
    ? state.cancelScheduled
      ? `<button type="button" class="btn btn-secondary btn-sm" data-action="resume-pro-subscription" ${resuming || busy ? 'disabled aria-busy="true"' : ''}>${resuming ? 'Resuming' : 'Resume subscription'}</button>`
      : `<button type="button" class="btn btn-secondary btn-sm" data-action="open-cancel-subscription" ${busy ? 'disabled' : ''}>Cancel subscription</button>`
    : '';

  // On Free the upgrade is the card's primary action (it sits next to the $5.99
  // value pitch); on Pro there's no upgrade button.
  const upgrade = state.plan === 'Free'
    ? `<button type="button" class="btn btn-primary btn-sm" data-action="start-pro-checkout" ${state.checkoutStatus === 'starting' ? 'disabled' : ''}>${state.checkoutStatus === 'starting' ? 'Starting checkout' : 'Upgrade to Pro'}</button>`
    : '';

  const portalOnlyHint = isPro && !state.subscriptionManageable
    ? '<p class="account-hint">This subscription was not created here, so the dashboard cannot drive it. Manage or cancel it from the subscription-management link in your Paddle receipt email, or write to billing@recued.com.</p>'
    : '';

  // Past-due dunning CTA — a warning banner + a primary "update payment method"
  // button that opens a fresh Paddle customer-portal session (deep-linked to
  // payment update). Non-manageable subs fall back to the hosted portal link.
  const dunning = isPastDue
    ? `<div class="flash flash-warn">Your last payment didn't go through. Paddle is retrying the charge${dateLabel ? ` (next attempt ${e(dateLabel)})` : ''} — update your payment method to keep Pro before the retries run out.</div>${
        state.subscriptionManageable
          ? `<div class="account-actions compact"><button type="button" class="btn btn-primary btn-sm" data-action="update-payment" ${opening ? 'disabled aria-busy="true"' : busy ? 'disabled' : ''}>${opening ? 'Opening billing portal' : 'Update payment method'}</button></div>`
          : ''
      }`
    : '';

  return `
        <section class="account-card account-section">
          <div class="account-section-head">
            <h3>Subscription and billing</h3>
            <span class="chip chip-plan chip-${chipClass(state.plan)}">${e(state.plan)}</span>
          </div>
          ${state.checkoutReturn === 'success'
            ? `<div class="flash flash-ok" data-checkout-return>Payment received. Your Pro subscription is being activated — this can take a moment to appear. <button type="button" class="btn-link" data-action="dismiss-checkout-return">Dismiss</button></div>`
            : state.checkoutReturn === 'cancelled'
              ? `<div class="flash flash-info" data-checkout-return>Checkout cancelled — no changes were made. <button type="button" class="btn-link" data-action="dismiss-checkout-return">Dismiss</button></div>`
              : ''}
          <div class="account-row">
            <span class="account-label">Tier</span>
            <span>${e(state.plan)}</span>
          </div>
          <div class="account-row">
            <span class="account-label">Status</span>
            <span>${e(statusText)}</span>
          </div>
          <div class="account-row">
            <span class="account-label">${e(dateRowLabel)}</span>
            <span>${e(dateLabel ?? 'Not scheduled')}</span>
          </div>
          ${state.cancelScheduled ? `<div class="flash flash-info">Your Pro plan is scheduled to end${dateLabel ? ` on ${e(dateLabel)}` : ''}. You keep Pro until then — resume any time before it ends.</div>` : ''}
          ${dunning}
          ${renderPlanDetails(state)}
          ${state.billingCancelConfirm && !state.cancelScheduled ? `
          <div class="danger-warning">
            <p><strong>Cancel Pro at the end of your billing period?</strong> You keep Pro features until ${dateLabel ? e(dateLabel) : 'the period ends'}, then drop to Free. Your <code>.recued.net</code> subdomain and certificate enter the grace period. You can resume any time before it ends.</p>
          </div>
          <div class="account-actions compact">
            <button type="button" class="btn btn-danger btn-sm" data-action="confirm-cancel-subscription" ${cancelling || busy ? 'disabled aria-busy="true"' : ''}>${cancelling ? 'Scheduling cancellation' : 'Cancel at period end'}</button>
            <button type="button" class="btn btn-secondary btn-sm" data-action="dismiss-cancel-subscription" ${busy ? 'disabled' : ''}>Keep Pro</button>
          </div>
          ` : `
          <div class="account-actions compact">
            ${renderManageBillingButton(state, 'btn-secondary')}
            ${selfServe}
            ${upgrade}
          </div>
          `}
          ${portalOnlyHint}
          ${renderBillingHistory(state)}
          ${state.checkoutMessage ? `<div class="flash flash-error">${e(state.checkoutMessage)}</div>` : ''}
          ${state.billingActionMessage ? `<div class="flash flash-error">${e(state.billingActionMessage)}</div>` : ''}
        </section>`;
};

const renderAccount = (state: DashboardState): string => {
  const publisherUrl = state.publisherHandle ? publisherPageUrl(state.publisherHandle) : null;
  return `
    <section class="dash-page account-page page-rail">
      <div class="page-rail-main">
      <div class="page-header">
        <h2>Account</h2>
        ${renderManageBillingButton(state, state.plan === 'Pro' ? 'btn-primary' : 'btn-secondary')}
      </div>
      <div class="account-stack">
        <section class="account-card account-section">
          <div class="account-section-head">
            <h3>Identity</h3>
            <button type="button" class="btn btn-secondary btn-sm" data-action="sign-out">Sign out</button>
          </div>
          <div class="account-row">
            <span class="account-label">Email</span>
            <span>${e(state.user?.email ?? '')}</span>
          </div>
          <div class="account-row">
            <span class="account-label">Account ID</span>
            <code>${e(state.user?.id ?? '')}</code>
          </div>
          <div class="account-row">
            <span class="account-label">Created</span>
            <span>${e(state.user?.created_at || 'Unknown')}</span>
          </div>
        </section>

        <section class="account-card account-section">
          <div class="account-section-head">
            <h3>Publisher namespace</h3>
            ${publisherUrl ? `<a href="${e(publisherUrl)}" target="_blank" rel="noopener" class="btn btn-secondary btn-sm">View publisher page</a>` : ''}
          </div>
          ${renderHandleRow(state)}
          <div class="account-row">
            <span class="account-label">Public URL</span>
            <span>${publisherUrl ? `<a href="${e(publisherUrl)}" target="_blank" rel="noopener">${e(publisherUrl)}</a>` : '<em>Claim a handle first</em>'}</span>
          </div>
          ${state.publisherHandle ? renderPublisherProfile(state) : renderPublisherSetup(state)}
        </section>

        ${renderSubscriptionCard(state)}

        ${renderProConvenienceStrip(state)}
        ${renderServerBindings(state)}
        ${renderBindingAudit(state)}

        <section class="account-card account-section">
          <h3>Security</h3>
          <div class="account-actions security-actions">
            <button type="button" class="btn btn-secondary btn-sm" data-action="sign-out">Sign out current browser</button>
            <button type="button" class="btn btn-secondary btn-sm" data-action="sign-out-all"
              ${state.signOutAllStatus === 'working' ? 'disabled aria-busy="true"' : ''}>${state.signOutAllStatus === 'working' ? 'Signing out everywhere' : 'Sign out all dashboard sessions'}</button>
          </div>
          <p class="account-hint">Rotate a bound server's credential from the Server bindings list — the binding survives; re-pair the server to issue a fresh credential.</p>
          ${state.signOutAllMessage ? `<div class="flash flash-error">${e(state.signOutAllMessage)}</div>` : ''}
        </section>

        ${renderDangerZone(state)}
      </div>
      </div>
      ${renderContextRail(state)}
    </section>
  `;
};

const renderDangerZone = (state: DashboardState): string => {
  const deleting = state.deleteAccountStatus === 'deleting';
  const email = state.user?.email ?? '';
  const confirmMatches = state.deleteAccountConfirm.trim().toLowerCase() === email.trim().toLowerCase() && email !== '';
  return `
    <section class="account-card account-section danger-zone">
      <h3>Danger zone</h3>
      ${!state.deleteAccountOpen ? `
        <div class="account-row">
          <span class="account-label">Delete account</span>
          <span>Permanently delete this cloud account and everything it owns.</span>
        </div>
        <div class="account-actions compact">
          <button type="button" class="btn btn-danger btn-sm" data-action="open-delete-account">Delete account</button>
        </div>
      ` : `
        <div class="danger-warning">
          <p><strong>This is immediate and irreversible.</strong> There is no recovery window. Deleting your account will:</p>
          <ul>
            <li>Permanently remove all your published recipes and packs from the marketplace. Copies already installed on users' servers keep working, but can never receive updates.</li>
            <li>Cancel your Pro subscription immediately, and release your DDNS subdomain and certificates. A reachable server using them will lose its cloud conveniences.</li>
            <li>Unbind all your servers and release your publisher handle <strong>for anyone to claim</strong>.</li>
            <li>Erase your cloud account, sign-in and billing records.</li>
          </ul>
          <p>Your self-hosted server and all data on it are unaffected — Recued keeps your data on machines you control, and this account never held any of it.</p>
          <p>Type your account email <code>${e(email)}</code> to confirm:</p>
        </div>
        <div class="publisher-form">
          <input type="text" data-field="delete-account-confirm" value="${e(state.deleteAccountConfirm)}"
            placeholder="${e(email)}" spellcheck="false" autocomplete="off" ${deleting ? 'disabled' : ''} />
        </div>
        <div class="account-actions compact">
          <button type="button" class="btn btn-danger btn-sm" data-action="confirm-delete-account"
            ${confirmMatches && !deleting ? '' : 'disabled'}>${deleting ? 'Deleting account' : 'Permanently delete account'}</button>
          <button type="button" class="btn btn-secondary btn-sm" data-action="cancel-delete-account" ${deleting ? 'disabled' : ''}>Cancel</button>
        </div>
      `}
      ${state.deleteAccountMessage ? `<div class="flash flash-error">${e(state.deleteAccountMessage)}</div>` : ''}
    </section>
  `;
};

// ────────────────────────────────────────────────────────────────
// Top-level renderer
// ────────────────────────────────────────────────────────────────

const renderSkipLink = (): string =>
  '<a class="dash-skip-link" href="#dashboard-main">Skip to dashboard content</a>';

export const renderDashboard = (state: DashboardState): string => {
  if (state.sessionStatus === 'checking') {
    return `
      <div class="dashboard">
        ${renderSkipLink()}
        ${renderHeader(state)}
        <main class="dash-main" id="dashboard-main">${renderPageSkeleton()}</main>
      </div>
    `;
  }
  if (state.sessionStatus === 'error') {
    return `
      <div class="dashboard">
        ${renderSkipLink()}
        ${renderHeader(state)}
        <main class="dash-main" id="dashboard-main">${renderSessionError(state)}</main>
      </div>
    `;
  }
  if (!state.user) {
    return `<div class="dashboard">${renderSkipLink()}${renderHeader(state)}${renderAuth(state)}</div>`;
  }
  let body = '';
  switch (state.page) {
    case 'home':       body = renderHome(state); break;
    case 'recipes':    body = renderMyRecipes(state); break;
    case 'packs':       body = renderMyPacks(state); break;
    case 'publish':      body = renderPublish(state); break;
    case 'account':     body = renderAccount(state); break;
    default:           body = renderHome(state);
  }
  return `
    <div class="dashboard">
      ${renderSkipLink()}
      ${renderHeader(state)}
      <main class="dash-main" id="dashboard-main">
        ${renderGlobalActions(state)}
        ${state.error ? `<div class="flash flash-error page-flash">${e(state.error)} <button type="button" class="btn btn-sm" data-action="retry-page">Retry</button></div>` : ''}
        ${body}
      </main>
      <footer class="dash-footer">
        <span>Recued holds the space between you and AI.</span>
        <nav class="dash-footer-links" aria-label="Footer">
          <a href="${MARKETPLACE_URL}" target="_blank" rel="noopener">Marketplace</a>
          <a href="https://recued.com/terms.html" target="_blank" rel="noopener">Terms</a>
          <a href="https://recued.com/privacy.html" target="_blank" rel="noopener">Privacy</a>
        </nav>
      </footer>
      ${state.copyStatus ? `<div class="copy-toast" role="status">${e(state.copyStatus)}</div>` : ''}
    </div>
  `;
};
